import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { useTier } from '../context/TierContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { ListViewHeader } from '../components/ui/list-view-header';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, List, LayoutGrid, CalendarDays, 
  AlertTriangle, Pencil, Search, MapPin, Building2, Truck, Users, Clock, MoreHorizontal, Copy, RefreshCw, Kanban, Wrench, FileText, Star, Ban, Sparkles, ChevronDown, ChevronUp, CheckCircle2, XCircle, MessageSquare, User, Eye, FileDown, Send, Check, X, ExternalLink, ClipboardCheck, AlertCircle, RotateCcw, Loader2, ArrowRight, CalendarPlus, Camera, Package
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { DatePicker } from '../components/ui/date-picker';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { format, addDays, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parse } from 'date-fns';
import { usePresence, isVersionConflict } from '../hooks/usePresence';
import { PresenceIndicator, VersionConflictAlert } from '../components/PresenceIndicator';

const Schedule = () => {
  const { api, user } = useAuth();
  const { openJobChat, canUseChat } = useChat();
  const { isAiAvailable } = useTier();
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allocations, setAllocations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [operators, setOperators] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tenantSettings, setTenantSettings] = useState({ 
    date_format: 'DD/MM/YYYY', 
    timezone: 'Australia/Sydney',
    currency: 'AUD',
    currency_symbol: '$',
    tax_name: 'GST',
    tax_rate: 10,
    industry: 'crane_hire'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [quickDateFilter, setQuickDateFilter] = useState('today'); // Default to Today
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [dateFrom, setDateFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Job Dialog
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [versionConflict, setVersionConflict] = useState(null); // For optimistic locking
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [jobForm, setJobForm] = useState({
    customer_id: '',
    contact_id: '',
    site_id: '',
    site_contact_id: '', // Site contact for this job
    // Structured one-off address fields
    one_off_street: '',
    one_off_suburb: '',
    one_off_state: '',
    one_off_postcode: '',
    use_one_off_address: false, // Toggle between site and one-off address
    equipment_type: '',
    attachment_type_ids: [],
    worker_requirements: [], // [{worker_type_id, quantity}]
    start_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '07:00',
    end_date: '',
    est_duration: '',
    description: '',
    status: 'not_allocated',
    notes: '',
    tags: [],
    po_number: ''
  });

  const [tagInput, setTagInput] = useState('');
  
  // Customer contacts state
  const [customerContacts, setCustomerContacts] = useState([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    position: ''
  });

  // Site contacts state
  const [siteContacts, setSiteContacts] = useState([]);

  // Track if selected customer requires PO
  const [selectedCustomerRequiresPO, setSelectedCustomerRequiresPO] = useState(false);

  // Presence tracking for job editing
  const { othersViewing: jobOthersViewing, removePresence: removeJobPresence } = usePresence(
    'job',
    editingJob?.id,
    { enabled: !!editingJob?.id }
  );

  // Australian states for dropdown
  const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

  // Allocation Dialog
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [selectedJobForAllocation, setSelectedJobForAllocation] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [allocationVersionConflict, setAllocationVersionConflict] = useState(null);
  const [overrideCheck, setOverrideCheck] = useState(false);
  const [allocationForm, setAllocationForm] = useState({
    equipment_id: '',
    operator_id: '',
    start_date: '',
    end_date: '',
    attachment_ids: [],
    worker_allocations: [], // [{worker_type_id, operator_id}]
    // Combo allocation fields
    is_combo_allocation: false,
    combo_equipment_ids: [] // [{component_type_id, equipment_id}]
  });
  
  // Presence tracking for allocation editing
  const { othersViewing: allocationOthersViewing, removePresence: removeAllocationPresence } = usePresence(
    'allocation',
    editingAllocation?.id,
    { enabled: !!editingAllocation?.id }
  );
  
  // Worker allocations state
  const [workerAllocations, setWorkerAllocations] = useState([]);
  
  // Allocation conflict warning state
  const [conflictWarnings, setConflictWarnings] = useState([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [pendingAllocationData, setPendingAllocationData] = useState(null);
  const [conflictContext, setConflictContext] = useState(null); // 'single' or 'multi'
  
  // Multi-day allocation management drawer
  const [multiDayDrawerOpen, setMultiDayDrawerOpen] = useState(false);
  const [multiDayJob, setMultiDayJob] = useState(null);
  const [multiDayAllocations, setMultiDayAllocations] = useState([]);
  const [multiDayStats, setMultiDayStats] = useState(null);
  const [loadingMultiDay, setLoadingMultiDay] = useState(false);
  const [editingMultiDayAllocation, setEditingMultiDayAllocation] = useState(null);
  const [multiDayAllocationForm, setMultiDayAllocationForm] = useState({
    start_date: '',
    end_date: '',
    operator_id: '',
    equipment_id: '',
    status: 'draft',
    attachment_ids: [],
    worker_allocations: [],
    is_combo_allocation: false,
    combo_equipment_ids: []
  });
  
  // Extend Allocations Modal (when job end date is extended)
  const [extendAllocationsModal, setExtendAllocationsModal] = useState({
    open: false,
    jobId: null,
    jobDescription: '',
    oldEndDate: null,
    newEndDate: null,
    allocations: [],
    pendingJobData: null
  });

  // Extend Job Modal (quick extend from list view)
  const [extendJobModal, setExtendJobModal] = useState({
    open: false,
    job: null,
    dateFrom: '',
    dateTo: '',
    allocations: [],
    extendAllocations: true,
    loading: false
  });

  // Delete Job Confirmation State
  const [deleteJobDialogOpen, setDeleteJobDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [isDeletingJob, setIsDeletingJob] = useState(false);
  
  // Smart Operator Suggestions
  const [operatorSuggestions, setOperatorSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false); // Collapsed by default

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);

  // Quick Add Customer Dialog
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: ''
  });

  // Quick Add Site Dialog
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [siteForm, setSiteForm] = useState({
    name: '',
    street_address: '',
    suburb: '',
    state: '',
    postcode: '',
    notes: ''
  });

  // Docket Dialog
  const [docketDialogOpen, setDocketDialogOpen] = useState(false);
  const [selectedJobForDocket, setSelectedJobForDocket] = useState(null);
  const [docketItems, setDocketItems] = useState([]);
  const [docketNotes, setDocketNotes] = useState('');
  const [docketWorkCompleted, setDocketWorkCompleted] = useState('');
  const [docketPaperUrl, setDocketPaperUrl] = useState('');
  const [docketPaperName, setDocketPaperName] = useState('');
  const [docketUploading, setDocketUploading] = useState(false);
  const [priceLists, setPriceLists] = useState([]);
  const [customerPriceList, setCustomerPriceList] = useState(null);
  const [selectedPriceListId, setSelectedPriceListId] = useState('');
  const [equipmentTypesWithIds, setEquipmentTypesWithIds] = useState([]);
  
  // Material Loads (for earthmoving industry)
  const [docketMaterialLoads, setDocketMaterialLoads] = useState([]);
  const [showMaterialLoadDialog, setShowMaterialLoadDialog] = useState(false);
  const [editingMaterialLoad, setEditingMaterialLoad] = useState(null);
  const [yards, setYards] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);

  const [viewDocketDrawerOpen, setViewDocketDrawerOpen] = useState(false);
  const [viewingDocket, setViewingDocket] = useState(null);
  const [jobDocketsList, setJobDocketsList] = useState([]);
  const [docketsListDrawerOpen, setDocketsListDrawerOpen] = useState(false);
  const [selectedJobForDocketsList, setSelectedJobForDocketsList] = useState(null);
  const [jobDocketsCache, setJobDocketsCache] = useState({}); // Cache dockets per job_id
  
  // Docket editing state for Invoice tab
  const [docketEditItems, setDocketEditItems] = useState([]);
  const [docketEditNotes, setDocketEditNotes] = useState('');
  const [docketSaving, setDocketSaving] = useState(false);
  const [docketRecalculating, setDocketRecalculating] = useState(false);
  const [docketUsedPriceList, setDocketUsedPriceList] = useState(null);

  const fetchData = async () => {
    try {
      const [allocRes, jobsRes, eqRes, opRes, custRes, sitesRes, typesRes, tenantRes, attachmentsRes, attachmentTypesRes, eqTypesAllRes, workerTypesRes, workerAllocRes] = await Promise.all([
        api.get('/allocations'),
        api.get('/jobs'),
        api.get('/equipment'),
        api.get('/operators'),
        api.get('/customers'),
        api.get('/sites'),
        api.get('/equipment-types'),
        api.get(`/tenants/${user?.client_id}`),
        api.get('/attachments'),
        api.get('/attachment-types'),
        api.get('/equipment-types/all'),
        api.get('/worker-types'),
        api.get('/worker-allocations')
      ]);
      setAllocations(allocRes.data);
      setJobs(jobsRes.data);
      setEquipment(eqRes.data);
      setOperators(opRes.data);
      setCustomers(custRes.data);
      setSites(sitesRes.data);
      setEquipmentTypes(typesRes.data);
      setWorkerTypes(workerTypesRes.data);
      setWorkerAllocations(workerAllocRes.data);
      setAttachments(attachmentsRes.data);
      setAttachmentTypes(attachmentTypesRes.data);
      setEquipmentTypesWithIds(eqTypesAllRes.data);
      if (tenantRes.data) {
        setTenantSettings({
          date_format: tenantRes.data.date_format || 'DD/MM/YYYY',
          timezone: tenantRes.data.timezone || 'Australia/Sydney',
          currency: tenantRes.data.currency || 'AUD',
          currency_symbol: tenantRes.data.currency_symbol || '$',
          tax_name: tenantRes.data.tax_name || 'GST',
          tax_rate: tenantRes.data.tax_rate ?? 10,
          industry: tenantRes.data.industry || 'crane_hire'
        });
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const fetchTags = async () => {
    try {
      const response = await api.get('/api/jobs/tags/all');
      setAvailableTags(response.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      toast.success('Data refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  // Format date according to tenant settings
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY':
        return `${day}-${monthShort}-${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const navigateWeek = (direction) => {
    setCurrentDate(prev => addDays(prev, direction * 7));
  };

  const navigateDay = (direction) => {
    setCurrentDate(prev => addDays(prev, direction));
  };

  // Helper functions
  const getJobInfo = (jobId) => jobs.find(j => j.id === jobId);
  const getEquipmentInfo = (eqId) => equipment.find(e => e.id === eqId);
  const getOperatorInfo = (opId) => operators.find(o => o.id === opId);
  const getCustomerInfo = (custId) => customers.find(c => c.id === custId);
  const getSiteInfo = (siteId) => sites.find(s => s.id === siteId);
  const getCustomerSites = (customerId) => sites.filter(s => s.customer_id === customerId);
  const getJobAllocation = (jobId) => allocations.find(a => a.job_id === jobId);
  const getAttachmentTypeName = (typeId) => attachmentTypes.find(t => t.id === typeId)?.name || '';
  
  // Check if operator is allocated to another job during a date range
  const getOperatorConflicts = (operatorId, startDate, endDate, excludeJobId = null) => {
    if (!operatorId || !startDate) return [];
    const effectiveEndDate = endDate || startDate;
    
    return allocations.filter(alloc => {
      if (alloc.operator_id !== operatorId) return false;
      if (excludeJobId && alloc.job_id === excludeJobId) return false;
      
      const allocStart = alloc.start_date;
      const allocEnd = alloc.end_date || alloc.start_date;
      
      // Check for date overlap
      return allocStart <= effectiveEndDate && allocEnd >= startDate;
    }).map(alloc => {
      const job = jobs.find(j => j.id === alloc.job_id);
      const customer = job ? getCustomerInfo(job.customer_id) : null;
      return {
        job_id: alloc.job_id,
        start_date: alloc.start_date,
        customer_name: customer?.company_name || 'Unknown',
        job_description: job?.description || ''
      };
    });
  };
  
  // Format site address from structured fields or legacy address
  const formatSiteAddress = (site) => {
    if (!site) return '';
    // Try structured address first
    if (site.street_address || site.suburb) {
      const parts = [site.street_address, site.suburb, site.state, site.postcode].filter(Boolean);
      return parts.join(', ');
    }
    // Fall back to legacy address field
    return site.address || '';
  };
  
  // Get job location - either from site or one-off address
  const getJobLocation = (job) => {
    if (job.site_id) {
      const site = getSiteInfo(job.site_id);
      return {
        name: site?.name || 'Unknown Site',
        address: formatSiteAddress(site),
        suburb: site?.suburb || '',
        isOneOff: false
      };
    } else if (job.one_off_street || job.one_off_suburb) {
      // Build formatted address from structured fields
      const addressParts = [job.one_off_street, job.one_off_suburb, job.one_off_state, job.one_off_postcode].filter(Boolean);
      return {
        name: 'One-off Location',
        address: addressParts.join(', '),
        suburb: job.one_off_suburb || '',
        isOneOff: true
      };
    }
    return { name: 'No Location', address: '', suburb: '', isOneOff: false };
  };
  
  // Check if an equipment type is a combo type
  const getEquipmentTypeInfo = (equipmentTypeName) => {
    if (!equipmentTypeName || !equipmentTypesWithIds?.length) return null;
    return equipmentTypesWithIds.find(et => et.name === equipmentTypeName);
  };
  
  const isComboEquipmentType = (equipmentTypeName) => {
    const eqType = getEquipmentTypeInfo(equipmentTypeName);
    return eqType?.is_combo || false;
  };
  
  // Get combo component types for a combo equipment type
  const getComboComponents = (equipmentTypeName) => {
    const eqType = getEquipmentTypeInfo(equipmentTypeName);
    if (!eqType?.is_combo || !eqType?.combo_component_ids?.length) return [];
    
    // Map component IDs to their equipment type objects, filtering out any that don't exist
    return eqType.combo_component_ids
      .map(id => equipmentTypesWithIds.find(et => et.id === id))
      .filter(comp => comp && comp.id && comp.name); // Ensure component has required fields
  };
  
  // Get attachment types filtered by equipment type
  const getFilteredAttachmentTypes = (equipmentType) => {
    if (!equipmentType) return attachmentTypes;
    
    // Find the equipment type ID from the name
    const eqType = equipmentTypesWithIds.find(et => et.name === equipmentType);
    if (!eqType) return attachmentTypes;
    
    // Return attachment types that either:
    // 1. Have no equipment_type_ids (universal attachments)
    // 2. Include this equipment type's ID
    return attachmentTypes.filter(at => 
      !at.equipment_type_ids || 
      at.equipment_type_ids.length === 0 || 
      at.equipment_type_ids.includes(eqType.id)
    );
  };
  
  // Get allocated attachment names as a formatted string for display
  const getAllocatedAttachmentsDisplay = (jobId) => {
    const allocation = getJobAllocation(jobId);
    if (!allocation?.attachment_ids?.length) return '';
    const attachmentNames = allocation.attachment_ids
      .map(id => attachments.find(a => a.id === id)?.name)
      .filter(Boolean);
    return attachmentNames.length ? `(${attachmentNames.join(', ')})` : '';
  };

  const availableEquipment = equipment.filter(e => e.status === 'available');
  const availableOperators = operators.filter(o => o.is_available);

  // Get allocations for a specific date (for calendar view)
  const getAllocationsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return allocations.filter(alloc => {
      const startDate = alloc.start_date;
      const endDate = alloc.end_date || alloc.start_date;
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  // Get combo equipment display for an allocation
  const getComboEquipmentDisplay = (allocation) => {
    if (!allocation?.is_combo_allocation || !allocation?.combo_equipment_ids?.length) {
      return null;
    }
    return allocation.combo_equipment_ids.map(comp => {
      const eq = equipment.find(e => e.id === comp.equipment_id);
      return eq ? eq.asset_id : comp.component_type_name;
    }).join(' + ');
  };

  // Get all jobs for a specific date (including unallocated)
  const getJobsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return jobs.filter(job => {
      const startDate = job.start_date;
      const endDate = job.end_date || job.start_date;
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  // Get date range based on quick filter
  const getQuickFilterDateRange = () => {
    const today = new Date();
    switch (quickDateFilter) {
      case 'today':
        return { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
      case 'yesterday':
        const yesterday = subDays(today, 1);
        return { from: format(yesterday, 'yyyy-MM-dd'), to: format(yesterday, 'yyyy-MM-dd') };
      case 'last_week':
        const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        return { from: format(lastWeekStart, 'yyyy-MM-dd'), to: format(lastWeekEnd, 'yyyy-MM-dd') };
      case 'this_week':
        const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
        return { from: format(thisWeekStart, 'yyyy-MM-dd'), to: format(thisWeekEnd, 'yyyy-MM-dd') };
      case 'next_30':
        return { from: format(today, 'yyyy-MM-dd'), to: format(addDays(today, 30), 'yyyy-MM-dd') };
      case 'all':
        return { from: null, to: null };
      default:
        return { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
    }
  };

  // Filter jobs for list view
  const filteredJobs = jobs.filter(job => {
    const customer = getCustomerInfo(job.customer_id);
    const matchesSearch = 
      job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    
    // Get job date range
    const jobStartDate = job.start_date;
    const jobEndDate = job.end_date || job.start_date;
    
    // Quick date filter (always applied unless 'all')
    let matchesQuickDate = true;
    if (quickDateFilter !== 'all') {
      const { from, to } = getQuickFilterDateRange();
      if (from && to) {
        // Job matches if its date range overlaps with the filter range
        matchesQuickDate = jobStartDate <= to && jobEndDate >= from;
      }
    }
    
    // Custom date range filter (when enabled)
    let matchesCustomDate = true;
    if (dateFilterEnabled) {
      // Job matches if its date range overlaps with the custom date range
      matchesCustomDate = jobStartDate <= dateTo && jobEndDate >= dateFrom;
    }
    
    return matchesSearch && matchesStatus && matchesQuickDate && matchesCustomDate;
  });

  // Job CRUD handlers
  const resetJobForm = () => {
    setJobForm({
      customer_id: '',
      contact_id: '',
      site_id: '',
      site_contact_id: '',
      one_off_street: '',
      one_off_suburb: '',
      one_off_state: '',
      one_off_postcode: '',
      use_one_off_address: false,
      equipment_type: '',
      attachment_type_ids: [],
      worker_requirements: [],
      start_date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '07:00',
      end_date: '',
      est_duration: '',
      description: '',
      status: 'not_allocated',
      notes: '',
      tags: [],
      po_number: ''
    });
    setEditingJob(null);
    setSelectedCustomerRequiresPO(false);
    setCustomerContacts([]);
    setSiteContacts([]);
  };

  // Fetch site contacts when a site is selected
  const fetchSiteContacts = async (siteId) => {
    if (!siteId) {
      setSiteContacts([]);
      return;
    }
    try {
      const response = await api.get(`/sites/${siteId}/contacts`);
      setSiteContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch site contacts:', error);
      setSiteContacts([]);
    }
  };

  const handleOpenJobDialog = (job = null, date = null) => {
    setVersionConflict(null); // Clear any previous conflict
    fetchTags(); // Fetch available tags for autocomplete
    if (job) {
      setEditingJob(job);
      // Check if customer requires PO
      const customer = customers.find(c => c.id === job.customer_id);
      setSelectedCustomerRequiresPO(customer?.requires_po || false);
      // Fetch contacts for this customer
      if (job.customer_id) {
        fetchCustomerContacts(job.customer_id);
      }
      // Fetch site contacts if a site is selected
      if (job.site_id) {
        fetchSiteContacts(job.site_id);
      } else {
        setSiteContacts([]);
      }
      const hasOneOffAddress = (job.one_off_street || job.one_off_suburb) && !job.site_id;
      setJobForm({
        customer_id: job.customer_id,
        contact_id: job.contact_id || '',
        site_id: job.site_id || '',
        site_contact_id: job.site_contact_id || '',
        one_off_street: job.one_off_street || '',
        one_off_suburb: job.one_off_suburb || '',
        one_off_state: job.one_off_state || '',
        one_off_postcode: job.one_off_postcode || '',
        use_one_off_address: hasOneOffAddress,
        equipment_type: job.equipment_type || '',
        attachment_type_ids: job.attachment_type_ids || [],
        worker_requirements: job.worker_requirements || [],
        start_date: job.start_date,
        start_time: job.start_time || '07:00',
        end_date: job.end_date || '',
        est_duration: job.est_duration || '',
        description: job.description,
        status: job.status,
        notes: job.notes || '',
        tags: job.tags || [],
        po_number: job.po_number || ''
      });
    } else {
      resetJobForm();
      if (date) {
        setJobForm(prev => ({ ...prev, start_date: format(date, 'yyyy-MM-dd') }));
      }
    }
    setJobDialogOpen(true);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !jobForm.tags.includes(tagInput.trim())) {
      setJobForm({ ...jobForm, tags: [...jobForm.tags, tagInput.trim()] });
      setTagInput('');
      // Refresh tags list in the background to include the new tag for future autocomplete
      fetchTags();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setJobForm({ ...jobForm, tags: jobForm.tags.filter(tag => tag !== tagToRemove) });
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRefreshAndReload = async () => {
    setVersionConflict(null);
    await fetchData();
    // Reload the job being edited with fresh data
    if (editingJob) {
      const freshJob = jobs.find(j => j.id === editingJob.id);
      if (freshJob) {
        handleOpenJobDialog(freshJob);
      } else {
        setJobDialogOpen(false);
        resetJobForm();
        toast.info('Job may have been deleted');
      }
    }
  };

  const handleSubmitJob = async (e) => {
    e.preventDefault();
    setVersionConflict(null);
    
    // Validate required fields
    if (!jobForm.customer_id) {
      toast.error('Customer is required');
      return;
    }
    if (!jobForm.equipment_type) {
      toast.error('Asset Type is required');
      return;
    }
    if (!jobForm.start_date) {
      toast.error('Start Date is required');
      return;
    }
    if (!jobForm.start_time) {
      toast.error('Start Time is required');
      return;
    }
    if (!jobForm.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    // Location validation: either site_id or one-off address must be provided
    if (!jobForm.use_one_off_address && !jobForm.site_id) {
      toast.error('Location is required - select a site or use a one-off address');
      return;
    }
    if (jobForm.use_one_off_address && !jobForm.one_off_street?.trim()) {
      toast.error('Street address is required for one-off locations');
      return;
    }
    
    try {
      const submitData = { ...jobForm };
      if (!submitData.end_date) submitData.end_date = null;
      if (!submitData.est_duration && submitData.est_duration !== 0) submitData.est_duration = null;
      
      // Handle site vs one-off address
      if (submitData.use_one_off_address) {
        submitData.site_id = null;
      } else {
        // Clear one-off fields when using site
        submitData.one_off_street = null;
        submitData.one_off_suburb = null;
        submitData.one_off_state = null;
        submitData.one_off_postcode = null;
      }
      delete submitData.use_one_off_address; // Don't send to backend
      
      // For new jobs, always set status to not_allocated
      if (!editingJob) {
        submitData.status = 'not_allocated';
      }
      
      if (editingJob) {
        // Check if end_date is being extended and there are existing allocations
        const oldEndDate = editingJob.end_date;
        const newEndDate = submitData.end_date;
        const isExtendingEndDate = newEndDate && (!oldEndDate || newEndDate > oldEndDate);
        
        if (isExtendingEndDate) {
          // Check for existing allocations that might need extending
          try {
            const allocResponse = await api.get(`/jobs/${editingJob.id}/allocations`);
            const existingAllocations = allocResponse.data.allocations || [];
            
            // Find allocations that end before the new job end date
            const allocationsToExtend = existingAllocations.filter(alloc => {
              const allocEndDate = alloc.end_date || alloc.start_date;
              return allocEndDate < newEndDate;
            });
            
            if (allocationsToExtend.length > 0) {
              // Show modal to ask user if they want to extend allocations
              setExtendAllocationsModal({
                open: true,
                jobId: editingJob.id,
                jobDescription: submitData.description || editingJob.description,
                oldEndDate: oldEndDate || editingJob.start_date,
                newEndDate: newEndDate,
                allocations: allocationsToExtend,
                pendingJobData: { ...submitData, version: editingJob.version }
              });
              return; // Don't proceed with job update yet
            }
          } catch (err) {
            // If we can't fetch allocations, proceed with normal update
            console.warn('Could not fetch allocations for extension check:', err);
          }
        }
        
        // Include version for optimistic locking
        submitData.version = editingJob.version;
        await api.put(`/jobs/${editingJob.id}`, submitData);
        toast.success('Job updated');
      } else {
        await api.post('/jobs', submitData);
        toast.success('Job created');
      }
      setJobDialogOpen(false);
      resetJobForm();
      removeJobPresence();
      fetchData();
    } catch (error) {
      // Check for version conflict (optimistic locking)
      if (isVersionConflict(error)) {
        const conflictData = error.response.data;
        setVersionConflict(conflictData);
        toast.error('This job was modified by another user');
      } else {
        toast.error(error.response?.data?.detail || 'Operation failed');
      }
    }
  };

  const handleDeleteJob = async (id) => {
    const job = jobs.find(j => j.id === id);
    setJobToDelete(job);
    setDeleteJobDialogOpen(true);
  };

  const handleConfirmDeleteJob = async () => {
    if (!jobToDelete) return;
    setIsDeletingJob(true);
    try {
      await api.delete(`/jobs/${jobToDelete.id}`);
      toast.success('Job deleted');
      fetchData();
      setDeleteJobDialogOpen(false);
      setJobToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeletingJob(false);
    }
  };

  // Handle extending allocations when job end date is extended
  const handleExtendAllocationsConfirm = async (extendAllocations) => {
    const { jobId, pendingJobData, allocations, newEndDate } = extendAllocationsModal;
    
    try {
      // First, update the job
      await api.put(`/jobs/${jobId}`, pendingJobData);
      
      // If user chose to extend allocations, update them
      if (extendAllocations && allocations.length > 0) {
        const updatePromises = allocations.map(alloc => 
          api.put(`/allocations/${alloc.id}`, {
            ...alloc,
            end_date: newEndDate
          })
        );
        
        await Promise.all(updatePromises);
        toast.success(`Job updated and ${allocations.length} allocation${allocations.length > 1 ? 's' : ''} extended`);
      } else {
        toast.success('Job updated');
      }
      
      // Close modal and reset
      setExtendAllocationsModal({
        open: false,
        jobId: null,
        jobDescription: '',
        oldEndDate: null,
        newEndDate: null,
        allocations: [],
        pendingJobData: null
      });
      setJobDialogOpen(false);
      resetJobForm();
      removeJobPresence();
      fetchData();
    } catch (error) {
      if (isVersionConflict(error)) {
        const conflictData = error.response.data;
        setVersionConflict(conflictData);
        toast.error('This job was modified by another user');
      } else {
        toast.error(error.response?.data?.detail || 'Operation failed');
      }
      // Close the extend modal but keep job dialog open
      setExtendAllocationsModal({
        open: false,
        jobId: null,
        jobDescription: '',
        oldEndDate: null,
        newEndDate: null,
        allocations: [],
        pendingJobData: null
      });
    }
  };

  const handleExtendAllocationsCancel = () => {
    setExtendAllocationsModal({
      open: false,
      jobId: null,
      jobDescription: '',
      oldEndDate: null,
      newEndDate: null,
      allocations: [],
      pendingJobData: null
    });
  };

  // Confirm job - makes it visible in mobile app
  const handleConfirmJob = async (job) => {
    try {
      await api.put(`/jobs/${job.id}`, {
        ...job,
        status: 'confirmed'
      });
      toast.success('Job confirmed - Now visible to operator');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm job');
    }
  };

  // Duplicate job handler
  const handleDuplicateJob = async (job) => {
    try {
      const duplicateData = {
        customer_id: job.customer_id,
        site_id: job.site_id || '',
        equipment_type: job.equipment_type,
        start_date: job.start_date,
        start_time: job.start_time || '07:00',  // Default to 7am if not set
        end_date: job.end_date || null,
        description: job.description || 'Duplicated job',
        status: 'not_allocated',
        notes: job.notes || ''
      };
      await api.post('/jobs', duplicateData);
      toast.success('Job duplicated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate job');
    }
  };

  // Open Extend Job modal
  const handleOpenExtendJob = async (job) => {
    // Calculate date from (current last day + 1)
    const lastDay = job.end_date || job.start_date;
    const lastDayDate = new Date(lastDay);
    lastDayDate.setDate(lastDayDate.getDate() + 1);
    const dateFrom = lastDayDate.toISOString().split('T')[0];
    
    // Fetch existing allocations
    let allocations = [];
    try {
      const response = await api.get(`/jobs/${job.id}/allocations`);
      allocations = response.data.allocations || [];
    } catch (err) {
      console.warn('Could not fetch allocations:', err);
    }
    
    setExtendJobModal({
      open: true,
      job: job,
      dateFrom: dateFrom,
      dateTo: '',
      allocations: allocations,
      extendAllocations: allocations.length > 0,
      loading: false
    });
  };

  // Handle Extend Job submit
  const handleExtendJobSubmit = async () => {
    const { job, dateTo, allocations, extendAllocations } = extendJobModal;
    
    if (!dateTo) {
      toast.error('Please select an end date');
      return;
    }
    
    setExtendJobModal(prev => ({ ...prev, loading: true }));
    
    try {
      // Update the job's end date - include all existing job fields to preserve data
      const jobUpdateData = {
        customer_id: job.customer_id,
        equipment_type: job.equipment_type,
        start_date: job.start_date,
        end_date: dateTo,
        description: job.description || '',
        status: job.status,
        version: job.version
      };
      
      // Include optional fields if they have values (preserve existing data)
      if (job.site_id) jobUpdateData.site_id = job.site_id;
      if (job.start_time) jobUpdateData.start_time = job.start_time;
      if (job.end_time) jobUpdateData.end_time = job.end_time;
      if (job.notes) jobUpdateData.notes = job.notes;
      if (job.po_number) jobUpdateData.po_number = job.po_number;
      if (job.job_type) jobUpdateData.job_type = job.job_type;
      if (job.worker_requirements) jobUpdateData.worker_requirements = job.worker_requirements;
      if (job.one_off_street) jobUpdateData.one_off_street = job.one_off_street;
      if (job.one_off_suburb) jobUpdateData.one_off_suburb = job.one_off_suburb;
      if (job.one_off_state) jobUpdateData.one_off_state = job.one_off_state;
      if (job.one_off_postcode) jobUpdateData.one_off_postcode = job.one_off_postcode;
      if (job.tags) jobUpdateData.tags = job.tags;
      
      await api.put(`/jobs/${job.id}`, jobUpdateData);
      
      // If user wants to extend allocations, update them
      if (extendAllocations && allocations.length > 0) {
        const updatePromises = allocations.map(alloc => {
          const allocUpdateData = {
            job_id: alloc.job_id,
            operator_id: alloc.operator_id,
            start_date: alloc.start_date,
            end_date: dateTo,
            status: alloc.status || 'confirmed'
          };
          if (alloc.equipment_id) allocUpdateData.equipment_id = alloc.equipment_id;
          return api.put(`/allocations/${alloc.id}`, allocUpdateData);
        });
        await Promise.all(updatePromises);
        toast.success(`Job extended and ${allocations.length} allocation${allocations.length > 1 ? 's' : ''} updated`);
      } else {
        toast.success('Job extended');
      }
      
      // Close modal and refresh
      setExtendJobModal({
        open: false,
        job: null,
        dateFrom: '',
        dateTo: '',
        allocations: [],
        extendAllocations: true,
        loading: false
      });
      fetchData();
    } catch (error) {
      // Handle error - detail could be string or object array
      let errorMessage = 'Failed to extend job';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Validation errors array
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
      setExtendJobModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Check if a job is multi-day
  const isMultiDayJob = (job) => {
    return job.end_date && job.end_date !== job.start_date;
  };

  // Fetch dockets for a specific job
  const fetchJobDockets = async (jobId) => {
    try {
      const res = await api.get(`/dockets/job/${jobId}`);
      const dockets = res.data || [];
      // Cache the result
      setJobDocketsCache(prev => ({ ...prev, [jobId]: dockets }));
      return dockets;
    } catch (error) {
      console.error('Failed to fetch job dockets:', error);
      return [];
    }
  };

  // Check if a job has any dockets submitted
  const jobHasDockets = async (jobId) => {
    // Check cache first
    if (jobDocketsCache[jobId] !== undefined) {
      return jobDocketsCache[jobId].length > 0;
    }
    const dockets = await fetchJobDockets(jobId);
    return dockets.length > 0;
  };

  // Handle View Docket(s) click
  const handleViewDockets = async (job) => {
    const dockets = await fetchJobDockets(job.id);
    
    if (dockets.length === 0) {
      toast.error('No dockets found for this job');
      return;
    }
    
    if (isMultiDayJob(job) && dockets.length > 1) {
      // Multi-day job with multiple dockets: show list of dockets
      setSelectedJobForDocketsList(job);
      setJobDocketsList(dockets);
      setDocketsListDrawerOpen(true);
    } else {
      // Single-day job OR multi-day with only one docket: open docket directly
      handleOpenViewDocket(dockets[0]);
    }
  };

  // Open a specific docket from the list
  const handleOpenDocketFromList = (docket) => {
    handleOpenViewDocket(docket);
  };

  // Docket action handlers for the drawer
  const handleSubmitDocketForApproval = async (docketId) => {
    try {
      await api.put(`/dockets/${docketId}`, { status: 'pending_approval' });
      toast.success('Docket submitted for approval');
      setViewDocketDrawerOpen(false);
      // Refresh dockets cache
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit docket');
    }
  };

  const handleApproveDocketFromDrawer = async (docketId) => {
    try {
      await api.put(`/dockets/${docketId}`, { 
        status: 'approved',
        approved_at: new Date().toISOString()
      });
      toast.success('Docket approved');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve docket');
    }
  };

  const handleRejectDocketFromDrawer = async (docketId) => {
    try {
      await api.put(`/dockets/${docketId}`, { status: 'draft' });
      toast.success('Docket returned to draft');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject docket');
    }
  };

  const handleGenerateDocketPDF = async (docket) => {
    try {
      const response = await api.get(`/dockets/${docket.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `docket_${docket.docket_number || docket.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleExportDocketToXero = async (docket) => {
    if (docket.xero_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to Xero');
      return;
    }
    try {
      const response = await api.post('/xero/push-invoice', { docket_id: docket.id });
      toast.success(`Invoice ${response.data.xero_invoice_number} created in Xero`);
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export to Xero');
    }
  };

  const handleExportDocketToMyob = async (docket) => {
    if (docket.myob_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to MYOB');
      return;
    }
    try {
      const response = await api.post('/myob/push-invoice', { docket_id: docket.id });
      toast.success(`Invoice created in MYOB`);
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export to MYOB');
    }
  };

  // Docket editing handlers for Invoice tab
  const handleOpenViewDocket = (docket) => {
    setViewingDocket(docket);
    setDocketEditItems(docket.items?.map(item => ({ ...item })) || []);
    setDocketEditNotes(docket.notes || '');
    setDocketUsedPriceList(null);
    setViewDocketDrawerOpen(true);
  };

  const handleDocketEditItemChange = (itemId, field, value) => {
    setDocketEditItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const handleDocketAddLineItem = () => {
    const newItem = {
      id: `new-${Date.now()}`,
      item_type: 'equipment',
      type_name: '',
      display_name: '',
      qty: 0,
      uom: 'HOUR',
      unit_price: 0,
      description: '',
      account_item_id: ''
    };
    setDocketEditItems(prev => [...prev, newItem]);
  };

  const handleDocketRemoveLineItem = (itemId) => {
    setDocketEditItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleDocketSaveEdit = async () => {
    if (!viewingDocket) return;
    setDocketSaving(true);
    try {
      await api.put(`/dockets/${viewingDocket.id}`, {
        items: docketEditItems,
        notes: docketEditNotes
      });
      toast.success('Docket updated');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update docket');
    } finally {
      setDocketSaving(false);
    }
  };

  const handleDocketRecalculatePrices = async () => {
    if (!viewingDocket) return;
    setDocketRecalculating(true);
    try {
      const response = await api.post(`/dockets/${viewingDocket.id}/recalculate-prices`);
      const recalculatedItems = response.data.items || [];
      const priceListName = response.data.price_list_name;
      setDocketEditItems(recalculatedItems);
      setDocketUsedPriceList(priceListName);
      toast.success(`Prices recalculated using "${priceListName}"`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate prices');
    } finally {
      setDocketRecalculating(false);
    }
  };

  // Calculate docket totals
  const calculateDocketSubtotal = (items) => {
    if (!items) return 0;
    return items.reduce((sum, item) => sum + (parseFloat(item.qty || item.quantity || 0) * parseFloat(item.unit_price || item.rate || 0)), 0);
  };

  const calculateDocketTax = (subtotal) => {
    if (tenantSettings.tax_name === 'None' || !tenantSettings.tax_rate) return 0;
    return subtotal * (tenantSettings.tax_rate / 100);
  };

  // Docket handlers
  const handleOpenDocketDialog = async (job) => {
    // Check if docket already exists
    try {
      const checkRes = await api.get(`/dockets/check/${job.id}/${job.start_date}`);
      if (checkRes.data.exists) {
        toast.error('A docket already exists for this job on this date');
        return;
      }
    } catch (error) {
      // Endpoint might not exist yet, continue
    }

    setSelectedJobForDocket(job);
    setDocketNotes('');
    setDocketWorkCompleted('');
    setDocketPaperUrl('');
    setDocketPaperName('');
    setDocketMaterialLoads([]);

    // Fetch fresh tenant data to get current industry setting
    let currentIndustry = tenantSettings.industry;
    try {
      const tenantRes = await api.get(`/tenants/${user?.client_id}`);
      if (tenantRes.data?.industry) {
        currentIndustry = tenantRes.data.industry;
        setTenantSettings(prev => ({
          ...prev,
          industry: tenantRes.data.industry
        }));
      }
    } catch (error) {
      console.error('Failed to fetch tenant industry:', error);
    }

    // Fetch yards and material types for material loads
    try {
      const [yardsRes, matTypesRes] = await Promise.all([
        api.get('/material-yards'),
        api.get('/material-types')
      ]);
      setYards(yardsRes.data || []);
      setMaterialTypes(matTypesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch yards/material types:', error);
    }

    // Build docket items (time-based, no pricing)
    buildDocketItems(job);
    setDocketDialogOpen(true);
  };

  // Function to build docket items without pricing (time-based like mobile)
  const buildDocketItems = (job) => {
    const items = [];
    
    // Equipment type item with default times (8am-5pm)
    items.push({
      id: `eq-${Date.now()}`,
      item_type: 'equipment',
      type_name: job.equipment_type,
      qty: 9,
      uom: 'HOUR',
      start_time: '08:00',
      finish_time: '17:00',
      lunch_minutes: 0,
      travel_minutes: 0
    });

    // Attachment type items with default times
    if (job.attachment_type_ids && job.attachment_type_ids.length > 0) {
      job.attachment_type_ids.forEach(attTypeId => {
        const attType = attachmentTypes.find(t => t.id === attTypeId);
        if (attType) {
          items.push({
            id: `att-${attTypeId}-${Date.now()}`,
            item_type: 'attachment',
            type_name: attType.name,
            qty: 9,
            uom: 'HOUR',
            start_time: '08:00',
            finish_time: '17:00',
            lunch_minutes: 0,
            travel_minutes: 0
          });
        }
      });
    }

    setDocketItems(items);
  };

  // Calculate hours from time inputs
  const calculateDocketHours = (startTime, finishTime, lunchMinutes = 0, travelMinutes = 0) => {
    try {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [finishHour, finishMin] = finishTime.split(':').map(Number);
      
      let startMinutes = startHour * 60 + startMin;
      let finishMinutes = finishHour * 60 + finishMin;
      
      // Handle overnight shifts
      if (finishMinutes < startMinutes) {
        finishMinutes += 24 * 60;
      }
      
      // Calculate: (Finish - Start) - Lunch + Travel
      const totalMinutes = finishMinutes - startMinutes - lunchMinutes + travelMinutes;
      return Math.round((totalMinutes / 60) * 100) / 100;
    } catch (error) {
      return 0;
    }
  };

  // Handle docket paper upload
  const handleDocketPaperUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setDocketUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/dockets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocketPaperUrl(response.data.url);
      setDocketPaperName(response.data.filename);
      toast.success('Paper docket uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setDocketUploading(false);
    }
  };

  // Handle delete paper docket
  const handleDeleteDocketPaper = async () => {
    if (!docketPaperUrl) return;
    
    try {
      // Extract filename from URL
      let filename = '';
      if (docketPaperUrl.includes('dockets/')) {
        const parts = docketPaperUrl.split('dockets/');
        if (parts.length > 1) {
          filename = 'dockets/' + parts[parts.length - 1].split('?')[0];
        }
      }
      
      if (filename) {
        const encodedFilename = filename.split('/').map(p => encodeURIComponent(p)).join('/');
        await api.delete(`/dockets/files/${encodedFilename}`);
      }
      
      setDocketPaperUrl('');
      setDocketPaperName('');
      toast.success('Paper docket removed');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete paper docket');
    }
  };

  // Material Loads handlers (earthmoving industry only)
  const handleAddMaterialLoad = () => {
    setEditingMaterialLoad({
      id: `load-${Date.now()}`,
      yard_id: '',
      yard_name: '',
      material_type_id: '',
      material_type_name: '',
      load_type: 'pickup', // pickup, disposal, buy_back
      quantity: 1,
      unit: 'tonne',
      tip_docket_number: '',
      tip_docket_photo_url: '',
      notes: ''
    });
    setShowMaterialLoadDialog(true);
  };

  const handleEditMaterialLoad = (load) => {
    setEditingMaterialLoad({ ...load });
    setShowMaterialLoadDialog(true);
  };

  const handleDeleteMaterialLoad = (loadId) => {
    setDocketMaterialLoads(prev => prev.filter(l => l.id !== loadId));
    toast.success('Material load removed');
  };

  const handleSaveMaterialLoad = () => {
    if (!editingMaterialLoad.yard_id) {
      toast.error('Please select a yard');
      return;
    }
    if (!editingMaterialLoad.material_type_id) {
      toast.error('Please select a material type');
      return;
    }
    if (!editingMaterialLoad.quantity || editingMaterialLoad.quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    const existingIndex = docketMaterialLoads.findIndex(l => l.id === editingMaterialLoad.id);
    if (existingIndex >= 0) {
      // Update existing
      setDocketMaterialLoads(prev => prev.map(l => 
        l.id === editingMaterialLoad.id ? editingMaterialLoad : l
      ));
    } else {
      // Add new
      setDocketMaterialLoads(prev => [...prev, editingMaterialLoad]);
    }

    setShowMaterialLoadDialog(false);
    setEditingMaterialLoad(null);
    toast.success('Material load saved');
  };

  const handleMaterialLoadYardChange = (yardId) => {
    const yard = yards.find(y => y.id === yardId);
    setEditingMaterialLoad(prev => ({
      ...prev,
      yard_id: yardId,
      yard_name: yard?.name || '',
      material_type_id: '', // Reset material type when yard changes
      material_type_name: ''
    }));
  };

  const handleMaterialLoadTypeChange = (typeId) => {
    const matType = materialTypes.find(t => t.id === typeId);
    setEditingMaterialLoad(prev => ({
      ...prev,
      material_type_id: typeId,
      material_type_name: matType?.name || ''
    }));
  };

  const handleTipDocketPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/dockets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditingMaterialLoad(prev => ({
        ...prev,
        tip_docket_photo_url: response.data.url
      }));
      toast.success('Tip docket photo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDocketItemChange = (itemId, field, value) => {
    setDocketItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      
      const updatedItem = { ...item, [field]: value };
      
      // Recalculate hours when time fields change
      if ((field === 'start_time' || field === 'finish_time' || field === 'lunch_minutes' || field === 'travel_minutes') && updatedItem.uom === 'HOUR') {
        if (updatedItem.start_time && updatedItem.finish_time) {
          updatedItem.qty = calculateDocketHours(
            updatedItem.start_time,
            updatedItem.finish_time,
            updatedItem.lunch_minutes || 0,
            updatedItem.travel_minutes || 0
          );
        }
      }
      
      return updatedItem;
    }));
  };

  const handleSubmitDocket = async (e) => {
    e.preventDefault();
    if (!selectedJobForDocket) return;

    // Validate at least one item has hours
    const filledItems = docketItems.filter(item => item.qty > 0);
    if (filledItems.length === 0) {
      toast.error('Please enter hours for at least one item');
      return;
    }

    try {
      const docketData = {
        job_id: selectedJobForDocket.id,
        job_date: selectedJobForDocket.start_date,
        customer_id: selectedJobForDocket.customer_id,
        site_id: selectedJobForDocket.site_id,
        equipment_type: selectedJobForDocket.equipment_type,
        notes: docketNotes,
        work_completed: docketWorkCompleted,
        paper_docket_url: docketPaperUrl || null,
        paper_docket_name: docketPaperName || null,
        items: docketItems.map(item => ({
          item_type: item.item_type,
          type_name: item.type_name,
          qty: parseFloat(item.qty) || 0,
          uom: item.uom,
          description: '',
          start_time: item.start_time || null,
          finish_time: item.finish_time || null,
          lunch_minutes: item.lunch_minutes || 0,
          travel_minutes: item.travel_minutes || 0
        })),
        material_loads: docketMaterialLoads.map(load => ({
          yard_id: load.yard_id,
          yard_name: load.yard_name,
          material_type_id: load.material_type_id,
          material_type_name: load.material_type_name,
          load_type: load.load_type,
          quantity: parseFloat(load.quantity) || 0,
          unit: load.unit,
          tip_docket_number: load.tip_docket_number || '',
          tip_docket_photo_url: load.tip_docket_photo_url || '',
          notes: load.notes || ''
        }))
      };

      await api.post('/dockets', docketData);
      toast.success('Docket created successfully');
      setDocketDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create docket');
    }
  };

  // Allocation handlers
  const handleOpenAllocationDialog = async (job) => {
    // Check if this is a multi-day job
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    
    if (isMultiDay) {
      // Open the multi-day allocation drawer instead
      handleOpenMultiDayDrawer(job);
      return;
    }
    
    // Single-day job - use existing dialog
    setSelectedJobForAllocation(job);
    setOverrideCheck(false); // Reset override when opening dialog
    setShowSuggestions(false); // Keep suggestions collapsed by default
    const existingAllocation = getJobAllocation(job.id);
    
    // Check if this is a combo equipment type
    const isCombo = isComboEquipmentType(job.equipment_type);
    const comboComponents = isCombo ? getComboComponents(job.equipment_type) : [];
    
    // Get existing worker allocations for this job
    const jobWorkerAllocations = workerAllocations.filter(wa => wa.job_id === job.id);
    const workerAllocationMap = jobWorkerAllocations.map(wa => ({
      worker_type_id: wa.worker_type_id,
      operator_id: wa.operator_id,
      id: wa.id // Keep ID for deletion
    }));
    
    // Helper to create combo equipment slots safely
    const createComboSlots = (components) => {
      return components.map(comp => ({
        component_type_id: comp?.id || '',
        component_type_name: comp?.name || 'Unknown',
        equipment_id: ''
      })).filter(slot => slot.component_type_id); // Only include valid slots
    };
    
    if (existingAllocation) {
      // Initialize combo equipment IDs from existing allocation or create empty slots
      let comboEquipIds = existingAllocation.combo_equipment_ids || [];
      
      // If we have existing combo equipment IDs but they're stored as strings (from backend),
      // we need to convert them back to objects with component type info
      if (isCombo && comboEquipIds.length > 0 && typeof comboEquipIds[0] === 'string') {
        // Map the stored equipment IDs to the combo component structure
        comboEquipIds = comboComponents.map((comp, index) => ({
          component_type_id: comp?.id || '',
          component_type_name: comp?.name || 'Unknown',
          equipment_id: comboEquipIds[index] || '' // Use stored equipment ID at same index
        }));
      } else if (isCombo && comboEquipIds.length === 0 && comboComponents.length > 0) {
        // Create empty slots for each combo component
        comboEquipIds = createComboSlots(comboComponents);
      }
      
      setAllocationForm({
        equipment_id: existingAllocation.equipment_id || '',
        operator_id: existingAllocation.operator_id || '',
        start_date: existingAllocation.start_date || job.start_date,
        end_date: existingAllocation.end_date || '',
        attachment_ids: existingAllocation.attachment_ids || [],
        worker_allocations: workerAllocationMap,
        is_combo_allocation: existingAllocation.is_combo_allocation || isCombo,
        combo_equipment_ids: comboEquipIds
      });
    } else {
      // Pre-populate worker allocations based on job requirements
      const initialWorkerAllocations = (job.worker_requirements || []).map(wr => ({
        worker_type_id: wr.worker_type_id,
        operator_id: '',
        quantity: wr.quantity
      }));
      
      // Create empty combo equipment slots for combo types
      const initialComboEquipIds = createComboSlots(comboComponents);
      
      setAllocationForm({
        equipment_id: '',
        operator_id: '',
        start_date: job.start_date || '',
        end_date: job.end_date || '',
        attachment_ids: [],
        worker_allocations: initialWorkerAllocations,
        is_combo_allocation: isCombo,
        combo_equipment_ids: initialComboEquipIds
      });
    }
    setAllocationDialogOpen(true);
    
    // Fetch smart operator suggestions
    fetchOperatorSuggestions(job);
  };

  // Helper to get job's worker allocations
  const getJobWorkerAllocations = (jobId) => {
    return workerAllocations.filter(wa => wa.job_id === jobId);
  };

  const fetchOperatorSuggestions = async (job) => {
    // Only fetch if AI is available
    if (!isAiAvailable()) {
      setOperatorSuggestions([]);
      return;
    }
    
    setSuggestionsLoading(true);
    try {
      const res = await api.get('/smart-suggestions/operators', {
        params: {
          job_id: job.id,
          customer_id: job.customer_id,
          site_id: job.site_id,
          equipment_type: job.equipment_type,
          job_date: job.start_date
        }
      });
      setOperatorSuggestions(res.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch operator suggestions', error);
      setOperatorSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const selectSuggestedOperator = (operatorId) => {
    setAllocationForm(prev => ({ ...prev, operator_id: operatorId }));
  };

  // Check for allocation conflicts before saving
  const checkAllocationConflicts = async (jobId, operatorId, equipmentId, startDate, endDate, workerAllocations = []) => {
    try {
      const response = await api.post('/allocations/check-conflicts', {
        job_id: jobId,
        operator_id: operatorId,
        equipment_id: equipmentId || '',
        start_date: startDate,
        end_date: endDate || startDate,
        worker_allocations: workerAllocations.filter(wa => wa.operator_id).map(wa => ({
          worker_type_id: wa.worker_type_id,
          operator_id: wa.operator_id
        }))
      });
      return response.data;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { has_conflicts: false, warnings: [] };
    }
  };

  // Proceed with allocation after conflict confirmation
  const proceedWithAllocation = async () => {
    setShowConflictDialog(false);
    
    if (conflictContext === 'single' && pendingAllocationData) {
      await executeAllocationSave(pendingAllocationData, true);
    } else if (conflictContext === 'multi' && pendingAllocationData) {
      await executeMultiDayAllocationSave(pendingAllocationData, true);
    }
    
    setPendingAllocationData(null);
    setConflictContext(null);
    setConflictWarnings([]);
  };

  // Execute single-day allocation save
  const executeAllocationSave = async (data, skipValidation = false) => {
    try {
      const { submitData, existingAllocation, worker_allocations, selectedJob } = data;
      const queryParam = skipValidation ? '?skip_validation=true' : '';

      if (existingAllocation) {
        await api.put(`/allocations/${existingAllocation.id}${queryParam}`, submitData);
        toast.success('Allocation updated');
      } else {
        await api.post(`/allocations${queryParam}`, submitData);
        if (selectedJob.status === 'not_allocated') {
          await api.put(`/jobs/${selectedJob.id}`, {
            ...selectedJob,
            status: 'allocated'
          });
        }
        toast.success('Allocation created - Ready to confirm');
      }

      // Handle worker allocations
      await api.delete(`/worker-allocations/job/${selectedJob.id}`);
      
      const workerAllocsToCreate = worker_allocations
        .filter(wa => wa.operator_id)
        .map(wa => ({
          job_id: selectedJob.id,
          worker_type_id: wa.worker_type_id,
          operator_id: wa.operator_id,
          start_date: submitData.start_date,
          end_date: submitData.end_date || submitData.start_date
        }));
      
      if (workerAllocsToCreate.length > 0) {
        await api.post('/worker-allocations/bulk', workerAllocsToCreate);
      }

      setAllocationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save allocation');
    }
  };

  const handleSubmitAllocation = async (e) => {
    if (e) e.preventDefault();
    try {
      // Validate required fields
      if (!allocationForm.start_date) {
        toast.error('Start Date is required');
        return;
      }
      
      const existingAllocation = getJobAllocation(selectedJobForAllocation.id);
      const { worker_allocations, ...mainAllocationData } = allocationForm;
      
      // For combo allocations, validate all component equipment is selected
      if (allocationForm.is_combo_allocation) {
        const missingComponents = (allocationForm.combo_equipment_ids || []).filter(c => !c?.equipment_id);
        if (missingComponents.length > 0) {
          toast.error('Please select equipment for all combo components');
          return;
        }
      }
      
      // Convert combo_equipment_ids from objects to just equipment ID strings for backend
      const comboEquipmentIdStrings = allocationForm.is_combo_allocation
        ? (allocationForm.combo_equipment_ids || []).map(c => c?.equipment_id).filter(Boolean)
        : [];
      
      const submitData = {
        job_id: selectedJobForAllocation.id,
        operator_id: allocationForm.operator_id,
        start_date: allocationForm.start_date,
        end_date: allocationForm.end_date || allocationForm.start_date,
        attachment_ids: allocationForm.attachment_ids || [],
        notes: allocationForm.notes || '',
        is_combo_allocation: allocationForm.is_combo_allocation,
        equipment_id: allocationForm.is_combo_allocation 
          ? (comboEquipmentIdStrings[0] || '')
          : allocationForm.equipment_id,
        combo_equipment_ids: comboEquipmentIdStrings
      };

      // If override is already checked, skip conflict check
      if (overrideCheck) {
        await executeAllocationSave({
          submitData,
          existingAllocation,
          worker_allocations,
          selectedJob: selectedJobForAllocation
        }, true);
        return;
      }

      // Check for conflicts first
      const conflictResult = await checkAllocationConflicts(
        selectedJobForAllocation.id,
        allocationForm.operator_id,
        allocationForm.is_combo_allocation ? comboEquipmentIdStrings[0] : allocationForm.equipment_id,
        allocationForm.start_date,
        allocationForm.end_date,
        worker_allocations
      );

      if (conflictResult.has_conflicts) {
        // Show conflict warning dialog
        setConflictWarnings(conflictResult.warnings);
        setPendingAllocationData({
          submitData,
          existingAllocation,
          worker_allocations,
          selectedJob: selectedJobForAllocation
        });
        setConflictContext('single');
        setShowConflictDialog(true);
      } else {
        // No conflicts, proceed directly
        await executeAllocationSave({
          submitData,
          existingAllocation,
          worker_allocations,
          selectedJob: selectedJobForAllocation
        }, false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save allocation');
    }
  };

  const handleDeleteAllocation = async (allocId) => {
    try {
      await api.delete(`/allocations/${allocId}`);
      // Also delete worker allocations
      if (selectedJobForAllocation) {
        await api.delete(`/worker-allocations/job/${selectedJobForAllocation.id}`);
      }
      toast.success('Allocation removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove allocation');
    }
  };

  // ========== MULTI-DAY ALLOCATION MANAGEMENT ==========
  
  const handleOpenMultiDayDrawer = async (job) => {
    // Close any other open drawers
    setJobDialogOpen(false);
    setAllocationDialogOpen(false);
    
    setMultiDayJob(job);
    setMultiDayDrawerOpen(true);
    setEditingMultiDayAllocation(null);
    await fetchMultiDayAllocations(job.id);
  };

  const fetchMultiDayAllocations = async (jobId) => {
    setLoadingMultiDay(true);
    try {
      const response = await api.get(`/jobs/${jobId}/allocations`);
      setMultiDayAllocations(response.data.allocations || []);
      setMultiDayStats(response.data.stats || null);
    } catch (error) {
      toast.error('Failed to fetch allocations');
      setMultiDayAllocations([]);
      setMultiDayStats(null);
    } finally {
      setLoadingMultiDay(false);
    }
  };

  const handleAddMultiDayAllocation = () => {
    if (!multiDayJob) return;
    
    // Find next unallocated date or use job start
    const nextDate = multiDayStats?.next_unallocated_date || multiDayJob.start_date;
    
    // Check if this is a combo equipment type
    const isCombo = isComboEquipmentType(multiDayJob.equipment_type);
    const comboComponents = isCombo ? getComboComponents(multiDayJob.equipment_type) : [];
    
    // Pre-populate worker allocations based on job requirements
    const initialWorkerAllocations = (multiDayJob.worker_requirements || []).map(wr => ({
      worker_type_id: wr.worker_type_id,
      operator_id: '',
      quantity: wr.quantity
    }));
    
    // Create empty combo equipment slots
    const initialComboEquipIds = comboComponents.map(comp => ({
      component_type_id: comp?.id || '',
      component_type_name: comp?.name || 'Unknown',
      equipment_id: ''
    })).filter(slot => slot.component_type_id);
    
    setMultiDayAllocationForm({
      start_date: nextDate,
      end_date: '',
      operator_id: '',
      equipment_id: '',
      status: 'draft',
      attachment_ids: [],
      worker_allocations: initialWorkerAllocations,
      is_combo_allocation: isCombo,
      combo_equipment_ids: initialComboEquipIds
    });
    setEditingMultiDayAllocation('new');
  };

  const handleEditMultiDayAllocation = (allocation) => {
    const isCombo = allocation.is_combo_allocation || isComboEquipmentType(multiDayJob?.equipment_type);
    const comboComponents = isCombo ? getComboComponents(multiDayJob?.equipment_type) : [];
    
    // Convert combo equipment IDs if needed
    let comboEquipIds = allocation.combo_equipment_ids || [];
    if (isCombo && comboEquipIds.length > 0 && typeof comboEquipIds[0] === 'string') {
      comboEquipIds = comboComponents.map((comp, index) => ({
        component_type_id: comp?.id || '',
        component_type_name: comp?.name || 'Unknown',
        equipment_id: comboEquipIds[index] || ''
      }));
    } else if (isCombo && comboEquipIds.length === 0 && comboComponents.length > 0) {
      comboEquipIds = comboComponents.map(comp => ({
        component_type_id: comp?.id || '',
        component_type_name: comp?.name || 'Unknown',
        equipment_id: ''
      })).filter(slot => slot.component_type_id);
    }
    
    setMultiDayAllocationForm({
      start_date: allocation.start_date || '',
      end_date: allocation.end_date || '',
      operator_id: allocation.operator_id || '',
      equipment_id: allocation.equipment_id || '',
      status: allocation.status || 'confirmed',
      attachment_ids: allocation.attachment_ids || [],
      worker_allocations: (allocation.worker_allocations || []).map(wa => ({
        worker_type_id: wa.worker_type_id,
        operator_id: wa.operator_id,
        id: wa.id
      })),
      is_combo_allocation: isCombo,
      combo_equipment_ids: comboEquipIds
    });
    setEditingMultiDayAllocation(allocation);
  };

  // Execute multi-day allocation save (after conflict confirmation or when no conflicts)
  const executeMultiDayAllocationSave = async (data, skipValidation = false) => {
    const { allocationData, worker_allocations, isNew, job, existingAllocation } = data;
    
    try {
      const queryParam = skipValidation ? '?skip_validation=true' : '';
      
      if (isNew) {
        const response = await api.post(`/allocations${queryParam}`, allocationData);
        const newAllocationId = response.data.id;
        
        for (const wa of worker_allocations) {
          if (wa.operator_id) {
            await api.post('/worker-allocations', {
              job_id: job.id,
              worker_type_id: wa.worker_type_id,
              operator_id: wa.operator_id,
              start_date: allocationData.start_date,
              end_date: allocationData.end_date,
              allocation_id: newAllocationId,
              status: allocationData.status
            });
          }
        }
        
        toast.success(allocationData.status === 'draft' 
          ? 'Draft allocation saved' 
          : 'Allocation confirmed and visible to operator'
        );
      } else {
        await api.put(`/allocations/${existingAllocation.id}${queryParam}`, allocationData);
        
        const existingWorkers = existingAllocation.worker_allocations || [];
        for (const ew of existingWorkers) {
          if (ew.id) {
            await api.delete(`/worker-allocations/${ew.id}`);
          }
        }
        
        for (const wa of worker_allocations) {
          if (wa.operator_id) {
            await api.post('/worker-allocations', {
              job_id: job.id,
              worker_type_id: wa.worker_type_id,
              operator_id: wa.operator_id,
              start_date: allocationData.start_date,
              end_date: allocationData.end_date,
              allocation_id: existingAllocation.id,
              status: allocationData.status
            });
          }
        }
        
        toast.success('Allocation updated');
      }
      
      setEditingMultiDayAllocation(null);
      await fetchMultiDayAllocations(job.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save allocation');
    }
  };

  const handleSaveMultiDayAllocation = async () => {
    if (!multiDayJob) return;
    
    const isNew = editingMultiDayAllocation === 'new';
    const { worker_allocations, ...mainData } = multiDayAllocationForm;
    
    // For combo allocations, validate all components
    if (mainData.is_combo_allocation) {
      const missingComponents = (mainData.combo_equipment_ids || []).filter(c => !c?.equipment_id);
      if (missingComponents.length > 0) {
        toast.error('Please select equipment for all combo components');
        return;
      }
    }
    
    // Prepare allocation data
    const allocationData = {
      job_id: multiDayJob.id,
      ...mainData,
      end_date: mainData.end_date || mainData.start_date,
      combo_equipment_ids: mainData.is_combo_allocation 
        ? mainData.combo_equipment_ids.map(c => c.equipment_id)
        : []
    };
    
    const saveData = {
      allocationData,
      worker_allocations,
      isNew,
      job: multiDayJob,
      existingAllocation: isNew ? null : editingMultiDayAllocation
    };
    
    // Check for conflicts first
    const conflictResult = await checkAllocationConflicts(
      multiDayJob.id,
      mainData.operator_id,
      mainData.is_combo_allocation 
        ? (mainData.combo_equipment_ids[0]?.equipment_id || '')
        : mainData.equipment_id,
      mainData.start_date,
      mainData.end_date,
      worker_allocations
    );

    if (conflictResult.has_conflicts) {
      // Show conflict warning dialog
      setConflictWarnings(conflictResult.warnings);
      setPendingAllocationData(saveData);
      setConflictContext('multi');
      setShowConflictDialog(true);
    } else {
      // No conflicts, proceed directly
      await executeMultiDayAllocationSave(saveData, false);
    }
  };

  const handleConfirmAllocation = async (allocationId) => {
    try {
      await api.post(`/allocations/${allocationId}/confirm`);
      toast.success('Allocation confirmed - operator can now see this job');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm allocation');
    }
  };

  const handleRevertToDraft = async (allocationId) => {
    try {
      await api.post(`/allocations/${allocationId}/revert-to-draft`);
      toast.success('Allocation reverted to draft - operator can no longer see this');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert allocation');
    }
  };

  const handleCopyAllocation = async (allocationId) => {
    if (!multiDayJob || !multiDayStats?.next_unallocated_date) {
      toast.error('No unallocated dates available');
      return;
    }
    
    try {
      await api.post(`/allocations/${allocationId}/copy`, {
        start_date: multiDayStats.next_unallocated_date,
        status: 'draft'
      });
      toast.success('Allocation copied to next available period');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy allocation');
    }
  };

  const handleDeleteMultiDayAllocation = async (allocationId) => {
    if (!confirm('Are you sure you want to delete this allocation?')) return;
    
    try {
      await api.delete(`/allocations/${allocationId}`);
      toast.success('Allocation deleted');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete allocation');
    }
  };

  // ========== END MULTI-DAY ALLOCATION ==========

  // Customer selection handler
  const handleCustomerSelect = (value) => {
    if (value === '__add_new__') {
      setCustomerForm({ company_name: '', contact_name: '', phone: '', email: '' });
      setCustomerDialogOpen(true);
    } else {
      // Check if customer requires PO
      const customer = customers.find(c => c.id === value);
      setSelectedCustomerRequiresPO(customer?.requires_po || false);
      setJobForm({ ...jobForm, customer_id: value, contact_id: '', site_id: '' });
      // Fetch contacts for the selected customer
      fetchCustomerContacts(value);
    }
  };

  // Fetch contacts for a customer
  const fetchCustomerContacts = async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}/contacts`);
      setCustomerContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setCustomerContacts([]);
    }
  };

  // Contact selection handler
  const handleContactSelect = (value) => {
    if (value === '__add_new__') {
      setContactForm({ name: '', phone: '', email: '', position: '' });
      setContactDialogOpen(true);
    } else {
      setJobForm({ ...jobForm, contact_id: value });
    }
  };

  // Create new contact
  const handleCreateContact = async (e) => {
    e.preventDefault();
    try {
      const contactData = {
        ...contactForm,
        customer_id: jobForm.customer_id
      };
      const response = await api.post(`/customers/${jobForm.customer_id}/contacts`, contactData);
      const newContact = response.data;
      toast.success('Contact created');
      setContactDialogOpen(false);
      // Refresh contacts and auto-select the new one
      await fetchCustomerContacts(jobForm.customer_id);
      setJobForm({ ...jobForm, contact_id: newContact.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    }
  };

  // Site selection handler
  const handleSiteSelect = async (value) => {
    if (value === '__add_new__') {
      setSiteForm({ name: '', address: '', notes: '' });
      setSiteDialogOpen(true);
    } else {
      setJobForm({ ...jobForm, site_id: value, site_contact_id: '' });
      // Fetch site contacts for the selected site
      await fetchSiteContacts(value);
    }
  };

  // Create new customer
  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/customers', customerForm);
      const newCustomer = response.data;
      toast.success('Customer created');
      setCustomerDialogOpen(false);
      const customersRes = await api.get('/customers');
      setCustomers(customersRes.data);
      setJobForm({ ...jobForm, customer_id: newCustomer.id, contact_id: '', site_id: '' });
      setCustomerContacts([]); // New customer has no contacts yet
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  // Create new site
  const handleCreateSite = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/sites', { ...siteForm, customer_id: jobForm.customer_id });
      const newSite = response.data;
      toast.success('Site created');
      setSiteDialogOpen(false);
      // Reset form
      setSiteForm({ name: '', street_address: '', suburb: '', state: '', postcode: '', notes: '' });
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.data);
      setJobForm({ ...jobForm, site_id: newSite.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create site');
    }
  };

  const getStatusBadge = (status) => {
    // Status colors use CSS variables that change based on theme
    const baseClass = "border";
    switch (status) {
      case 'not_allocated': 
        return <Badge variant="secondary" className={baseClass} style={{ backgroundColor: 'var(--status-not-allocated-bg)', color: 'var(--status-not-allocated-text)', borderColor: 'var(--status-not-allocated-border)' }}>Not Allocated</Badge>;
      case 'allocated': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-allocated-bg)', color: 'var(--status-allocated-text)', borderColor: 'var(--status-allocated-border)' }}>Allocated</Badge>;
      case 'confirmed': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-confirmed-bg)', color: 'var(--status-confirmed-text)', borderColor: 'var(--status-confirmed-border)' }}>Confirmed</Badge>;
      case 'docketed': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-docketed-bg)', color: 'var(--status-docketed-text)', borderColor: 'var(--status-docketed-border)' }}>Docketed</Badge>;
      case 'invoiced': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-invoiced-bg)', color: 'var(--status-invoiced-text)', borderColor: 'var(--status-invoiced-border)' }}>Invoiced</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get status color for calendar/card views
  // Light mode colors use Tailwind status colors, dark mode uses existing colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'not_allocated': return { 
        bg: 'bg-status-not-allocated-bg dark:bg-gray-500/20', 
        border: 'border-status-not-allocated-border dark:border-gray-500', 
        text: 'text-status-not-allocated-text dark:text-gray-400', 
        hover: 'hover:bg-red-200 dark:hover:bg-gray-500/30' 
      };
      case 'allocated': return { 
        bg: 'bg-status-allocated-bg dark:bg-amber-500/20', 
        border: 'border-status-allocated-border dark:border-amber-500', 
        text: 'text-status-allocated-text dark:text-amber-400', 
        hover: 'hover:bg-amber-200 dark:hover:bg-amber-500/30' 
      };
      case 'confirmed': return { 
        bg: 'bg-status-confirmed-bg dark:bg-blue-500/20', 
        border: 'border-status-confirmed-border dark:border-blue-500', 
        text: 'text-status-confirmed-text dark:text-blue-400', 
        hover: 'hover:bg-green-200 dark:hover:bg-blue-500/30' 
      };
      case 'docketed': return { 
        bg: 'bg-status-docketed-bg dark:bg-purple-500/20', 
        border: 'border-status-docketed-border dark:border-purple-500', 
        text: 'text-status-docketed-text dark:text-purple-400', 
        hover: 'hover:bg-purple-200 dark:hover:bg-purple-500/30' 
      };
      case 'invoiced': return { 
        bg: 'bg-status-invoiced-bg dark:bg-green-500/20', 
        border: 'border-status-invoiced-border dark:border-green-500', 
        text: 'text-status-invoiced-text dark:text-green-400', 
        hover: 'hover:bg-emerald-200 dark:hover:bg-green-500/30' 
      };
      default: return { bg: 'bg-gray-500/20', border: 'border-gray-500', text: 'text-gray-400', hover: 'hover:bg-gray-500/30' };
    }
  };

  // Calendar Job Card (shows any job, with allocation info if available)
  const JobCard = ({ job }) => {
    if (!job) return null;
    
    const allocation = getJobAllocation(job.id);
    const eq = allocation ? getEquipmentInfo(allocation.equipment_id) : null;
    const op = allocation ? getOperatorInfo(allocation.operator_id) : null;
    const customer = getCustomerInfo(job.customer_id);
    const attachmentsDisplay = getAllocatedAttachmentsDisplay(job.id);
    const statusColor = getStatusColor(job.status);
    const isAllocated = !!allocation;

    return (
      <div 
        className={`p-2 rounded-sm text-xs cursor-pointer border-l-4 ${statusColor.bg} ${statusColor.border} ${statusColor.hover}`}
        onClick={(e) => {
          e.stopPropagation();
          handleOpenJobDialog(job);
        }}
      >
        {job.start_time && (
          <div className={`font-bold mb-1 ${statusColor.text}`}>
            {job.start_time}
          </div>
        )}
        <div className="font-semibold truncate">{customer?.company_name || 'Unknown'}</div>
        {isAllocated ? (
          <>
            <div className="text-muted-foreground truncate">{eq?.equipment_type || eq?.name || 'No equipment'} {eq?.asset_id ? `(${eq.asset_id})` : ''}</div>
            <div className="text-muted-foreground truncate">{op?.name || 'No operator'}</div>
            {attachmentsDisplay && (
              <div className="text-muted-foreground truncate">{attachmentsDisplay}</div>
            )}
          </>
        ) : (
          <div 
            className="text-amber-500 truncate cursor-pointer hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAllocationDialog(job);
            }}
          >
            + Allocate
          </div>
        )}
      </div>
    );
  };

  // Legacy AllocationCard (kept for compatibility)
  const AllocationCard = ({ alloc }) => {
    const job = getJobInfo(alloc.job_id);
    const eq = getEquipmentInfo(alloc.equipment_id);
    const op = getOperatorInfo(alloc.operator_id);
    const customer = job ? getCustomerInfo(job.customer_id) : null;
    const attachmentsDisplay = getAllocatedAttachmentsDisplay(alloc.job_id);
    const statusColor = job ? getStatusColor(job.status) : getStatusColor('allocated');

    return (
      <div 
        className={`allocation-block ${statusColor.bg} border-l-4 ${statusColor.border} p-2 rounded-sm text-xs cursor-pointer ${statusColor.hover}`}
        onClick={() => job && handleOpenJobDialog(job)}
      >
        {job?.start_time && (
          <div className={`${statusColor.text} font-bold mb-1`}>{job.start_time}</div>
        )}
        <div className="font-semibold truncate">{customer?.company_name || 'Unknown'}</div>
        <div className="text-muted-foreground truncate">{eq?.equipment_type || eq?.name || 'No equipment'} {eq?.asset_id ? `(${eq.asset_id})` : ''}</div>
        <div className="text-muted-foreground truncate">{op?.name || 'No operator'}</div>
        {attachmentsDisplay && (
          <div className="text-muted-foreground truncate">{attachmentsDisplay}</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Jobs & Schedule</h1>
          <p className="text-muted-foreground">Manage jobs and allocate resources</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh} 
            disabled={refreshing}
            data-testid="refresh-btn"
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate(`/planning-board?date=${format(currentDate, 'yyyy-MM-dd')}`)}
            data-testid="planning-board-btn"
            title="Open Planning Board"
          >
            <Kanban className="h-4 w-4 mr-2" />
            Planning Board
          </Button>
          <Button className="btn-volt" onClick={() => handleOpenJobDialog()} data-testid="new-job-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Job
          </Button>
        </div>
      </div>

      {/* View Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="list" data-testid="view-list-btn">
              <List className="h-4 w-4 mr-1" />
              List
            </TabsTrigger>
            <TabsTrigger value="week" data-testid="view-week-btn">
              <LayoutGrid className="h-4 w-4 mr-1" />
              Week
            </TabsTrigger>
            <TabsTrigger value="board" data-testid="view-board-btn">
              <Kanban className="h-4 w-4 mr-1" />
              Board
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {(view === 'week' || view === 'board' || view === 'list') && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => {
              if (view === 'week') {
                navigateWeek(-1);
              } else if (view === 'list') {
                // In list view, navigate by day and set custom date filter to show that day
                const newDate = addDays(currentDate, -1);
                setCurrentDate(newDate);
                setQuickDateFilter('all'); // Disable quick filter
                setDateFilterEnabled(true); // Enable custom date range
                const dateStr = format(newDate, 'yyyy-MM-dd');
                setDateFrom(dateStr);
                setDateTo(dateStr);
              } else {
                navigateDay(-1);
              }
            }}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="font-heading font-semibold min-w-[200px] text-center">
              {view === 'week' 
                ? `${format(weekDays[0], 'MMM d')} - ${format(weekDays[6], 'MMM d, yyyy')}`
                : format(currentDate, 'EEEE, MMMM d, yyyy')
              }
            </div>
            <Button variant="outline" size="sm" onClick={() => {
              if (view === 'week') {
                navigateWeek(1);
              } else if (view === 'list') {
                // In list view, navigate by day and set custom date filter to show that day
                const newDate = addDays(currentDate, 1);
                setCurrentDate(newDate);
                setQuickDateFilter('all'); // Disable quick filter
                setDateFilterEnabled(true); // Enable custom date range
                const dateStr = format(newDate, 'yyyy-MM-dd');
                setDateFrom(dateStr);
                setDateTo(dateStr);
              } else {
                navigateDay(1);
              }
            }}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              setCurrentDate(new Date());
              if (view === 'list') {
                // In list view, set quick filter to today
                setQuickDateFilter('today');
                setDateFilterEnabled(false);
              }
            }}>
              Today
            </Button>
          </div>
        )}
      </div>

      {/* LIST VIEW */}
      {view === 'list' && (
        <Card className="card-industrial">
          <CardHeader className="pb-3">
            <ListViewHeader
              count={filteredJobs.length}
              countLabel="Jobs"
              showRefresh={false}
              
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search jobs..."
              searchTestId="job-search"
              
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              statusTestId="job-status-filter"
              statusOptions={[
                { value: 'all', label: 'All Status' },
                { value: 'not_allocated', label: 'Not Allocated' },
                { value: 'allocated', label: 'Allocated' },
                { value: 'confirmed', label: 'Confirmed' },
                { value: 'docketed', label: 'Docketed' },
                { value: 'invoiced', label: 'Invoiced' },
              ]}
              
              dateFilter={quickDateFilter}
              onDateFilterChange={setQuickDateFilter}
              dateFilterOptions={[
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'last_week', label: 'Last Week' },
                { value: 'this_week', label: 'This Week' },
                { value: 'next_30', label: 'Next 30 Days' },
                { value: 'all', label: 'All' },
              ]}
              
              showCustomDateRange={true}
              customDateEnabled={dateFilterEnabled}
              onCustomDateToggle={setDateFilterEnabled}
              dateFrom={dateFrom}
              onDateFromChange={setDateFrom}
              dateTo={dateTo}
              onDateToChange={setDateTo}
              formatDateDisplay={formatDateDisplay}
            />
          </CardHeader>
          <CardContent>
            {filteredJobs.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No jobs found</p>
                <Button className="mt-4 btn-volt" onClick={() => handleOpenJobDialog()}>
                  Create Your First Job
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="label-overline">Job #</TableHead>
                      <TableHead className="label-overline">Date</TableHead>
                      <TableHead className="label-overline">Customer</TableHead>
                      <TableHead className="label-overline">Type</TableHead>
                      <TableHead className="label-overline">Site</TableHead>
                      <TableHead className="label-overline">Description</TableHead>
                      <TableHead className="label-overline">Allocation</TableHead>
                      <TableHead className="label-overline">Workers</TableHead>
                      <TableHead className="label-overline">Status</TableHead>
                      <TableHead className="label-overline text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredJobs.map((job) => {
                      const customer = getCustomerInfo(job.customer_id);
                      const location = getJobLocation(job);
                      const allocation = getJobAllocation(job.id);
                      const allocEquipment = allocation ? getEquipmentInfo(allocation.equipment_id) : null;
                      const allocOperator = allocation ? getOperatorInfo(allocation.operator_id) : null;

                      return (
                        <TableRow 
                          key={job.id} 
                          data-testid={`job-row-${job.id}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleOpenJobDialog(job)}
                        >
                          <TableCell className="font-mono text-sm font-bold text-[#ffd108]">
                            {job.job_number ? `#${job.job_number}` : '-'}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            <div>{formatDateDisplay(job.start_date)}</div>
                            {job.start_time && (
                              <div className="text-[#ffd108]">{job.start_time}</div>
                            )}
                            {job.end_date && job.end_date !== job.start_date && (
                              <div className="text-muted-foreground text-xs">to {formatDateDisplay(job.end_date)}</div>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {customer?.company_name || 'Unknown'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {job.equipment_type || '-'}
                          </TableCell>
                          <TableCell>
                            {location.name !== 'No Location' ? (
                              <span className="flex items-center gap-1 text-muted-foreground text-sm">
                                <MapPin className="h-3 w-3" />
                                {location.isOneOff ? (
                                  <span className="flex items-center gap-1">
                                    <span className="text-[#ffd108] text-xs">📍</span>
                                    <span className="truncate max-w-[180px]" title={location.address}>
                                      {location.address || 'One-off Location'}
                                    </span>
                                  </span>
                                ) : (
                                  location.name
                                )}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">
                            {job.description}
                          </TableCell>
                          <TableCell 
                            className="cursor-pointer hover:bg-muted"
                            onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                          >
                            {allocation ? (
                              <div className="text-xs space-y-1">
                                <div className="flex items-center gap-1">
                                  <Truck className="h-3 w-3 text-[#ffd108]" />
                                  {allocEquipment ? `${allocEquipment.equipment_type || allocEquipment.name || 'Equipment'} / ${allocEquipment.asset_id}` : 'Unknown'}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="h-3 w-3 text-[#ffd108]" />
                                  {allocOperator?.name || 'Unknown'}
                                </div>
                                {getAllocatedAttachmentsDisplay(job.id) && (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Wrench className="h-3 w-3" />
                                    {getAllocatedAttachmentsDisplay(job.id)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-xs h-7"
                                onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Allocate
                              </Button>
                            )}
                          </TableCell>
                          {/* Workers Required Column */}
                          <TableCell>
                            {job.worker_requirements && job.worker_requirements.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {job.worker_requirements.map((wr, idx) => {
                                  const wt = workerTypes.find(t => t.id === wr.worker_type_id);
                                  return (
                                    <Badge 
                                      key={idx} 
                                      variant="outline" 
                                      className="text-xs px-1.5 py-0 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30"
                                    >
                                      {wr.quantity > 1 ? `${wr.quantity}x ` : ''}{wt?.name || 'Unknown'}
                                    </Badge>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(job.status)}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  data-testid={`job-actions-${job.id}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleOpenJobDialog(job); }}
                                  data-testid={`edit-job-${job.id}`}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  {job.status === 'docketed' || job.status === 'invoiced' ? 'View Job' : 'Edit Job'}
                                </DropdownMenuItem>
                                {/* Hide allocation editing for docketed/invoiced jobs */}
                                {job.status !== 'docketed' && job.status !== 'invoiced' && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                                    data-testid={`allocate-job-${job.id}`}
                                  >
                                    <Truck className="h-4 w-4 mr-2 text-[#ffd108]" />
                                    {allocation ? 'Edit Allocation' : 'Allocate Job'}
                                  </DropdownMenuItem>
                                )}
                                {/* View allocation (read-only) for docketed/invoiced jobs */}
                                {(job.status === 'docketed' || job.status === 'invoiced') && allocation && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                                    data-testid={`view-allocation-${job.id}`}
                                  >
                                    <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
                                    View Allocation
                                  </DropdownMenuItem>
                                )}
                                {job.status === 'allocated' && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleConfirmJob(job); }}
                                    data-testid={`confirm-job-${job.id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-2 text-blue-500" />
                                    Confirm Job
                                  </DropdownMenuItem>
                                )}
                                {job.status === 'confirmed' && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleOpenDocketDialog(job); }}
                                    data-testid={`create-docket-${job.id}`}
                                  >
                                    <FileText className="h-4 w-4 mr-2 text-green-500" />
                                    Create Docket
                                  </DropdownMenuItem>
                                )}
                                {/* View Docket(s) - shown for docketed/invoiced OR confirmed multi-day jobs (which might have partial dockets) */}
                                {(job.status === 'docketed' || job.status === 'invoiced' || (job.status === 'confirmed' && isMultiDayJob(job))) && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleViewDockets(job); }}
                                    data-testid={`view-dockets-${job.id}`}
                                  >
                                    <Eye className="h-4 w-4 mr-2 text-purple-500" />
                                    {isMultiDayJob(job) ? 'View Dockets' : 'View Docket'}
                                  </DropdownMenuItem>
                                )}
                                {canUseChat && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); openJobChat(job.id); }}
                                    data-testid={`job-chat-${job.id}`}
                                  >
                                    <MessageSquare className="h-4 w-4 mr-2 text-blue-500" />
                                    Job Chat
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleDuplicateJob(job); }}
                                  data-testid={`duplicate-job-${job.id}`}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicate Job
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleOpenExtendJob(job); }}
                                  data-testid={`extend-job-${job.id}`}
                                >
                                  <CalendarPlus className="h-4 w-4 mr-2" />
                                  Extend Job
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id); }}
                                  className="text-destructive focus:text-destructive"
                                  data-testid={`delete-job-${job.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Job
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* WEEK VIEW */}
      {view === 'week' && (
        <Card className="card-industrial overflow-hidden">
          <div className="grid grid-cols-7 border-b">
            {weekDays.map((day) => (
              <div 
                key={day.toISOString()} 
                className={`p-2 text-center border-r last:border-r-0 ${
                  isSameDay(day, new Date()) ? 'bg-[#ffd108]/10' : ''
                }`}
              >
                <div className="label-overline">{format(day, 'EEE')}</div>
                <div className={`font-heading text-lg ${
                  isSameDay(day, new Date()) ? 'text-[#ffd108] font-bold' : ''
                }`}>
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 min-h-[400px]">
            {weekDays.map((day) => {
              const dayJobs = getJobsForDate(day);
              return (
                <div 
                  key={day.toISOString()}
                  className={`border-r last:border-r-0 p-2 calendar-cell cursor-pointer hover:bg-muted/50 transition-colors ${
                    isSameDay(day, new Date()) ? 'bg-[#ffd108]/5' : ''
                  }`}
                  onClick={() => handleOpenJobDialog(null, day)}
                  data-testid={`calendar-cell-${format(day, 'yyyy-MM-dd')}`}
                >
                  <div className="space-y-1">
                    {dayJobs.map((job) => (
                      <JobCard key={job.id} job={job} />
                    ))}
                    {dayJobs.length === 0 && (
                      <div className="text-xs text-muted-foreground text-center py-4">
                        Click to add
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* BOARD/KANBAN VIEW */}
      {view === 'board' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Swim Lanes by Status */}
          {[
            { status: 'not_allocated', label: 'Not Allocated', colorVar: '--status-not-allocated-border', bgVar: '--status-not-allocated-bg' },
            { status: 'allocated', label: 'Allocated', colorVar: '--status-allocated-border', bgVar: '--status-allocated-bg' },
            { status: 'confirmed', label: 'Confirmed', colorVar: '--status-confirmed-border', bgVar: '--status-confirmed-bg' },
            { status: 'docketed', label: 'Docketed', colorVar: '--status-docketed-border', bgVar: '--status-docketed-bg' },
            { status: 'invoiced', label: 'Invoiced', colorVar: '--status-invoiced-border', bgVar: '--status-invoiced-bg' }
          ].map(({ status, label, colorVar, bgVar }) => {
            // Filter jobs for this status and selected date
            const laneJobs = jobs.filter(job => {
              const matchesStatus = job.status === status;
              const jobStartDate = job.start_date;
              const jobEndDate = job.end_date || job.start_date;
              const selectedDateStr = format(currentDate, 'yyyy-MM-dd');
              // Job shows if selected date falls within job's date range
              return matchesStatus && jobStartDate <= selectedDateStr && jobEndDate >= selectedDateStr;
            });

            return (
              <div key={status} className="rounded-lg p-3 min-h-[400px]" style={{ backgroundColor: `var(${bgVar})` }}>
                {/* Lane Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: `var(${colorVar})` }} />
                    <h3 className="font-heading font-semibold text-sm">{label}</h3>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {laneJobs.length}
                  </Badge>
                </div>

                {/* Job Cards */}
                <div className="space-y-3">
                  {laneJobs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No jobs
                    </div>
                  ) : (
                    laneJobs.map(job => {
                      const allocation = getJobAllocation(job.id);
                      const eq = allocation ? getEquipmentInfo(allocation.equipment_id) : null;
                      const op = allocation ? getOperatorInfo(allocation.operator_id) : null;
                      const customer = getCustomerInfo(job.customer_id);
                      const location = getJobLocation(job);

                      return (
                        <div 
                          key={job.id} 
                          className="bg-card border border-border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => handleOpenJobDialog(job)}
                        >
                          {/* Time */}
                          {job.start_time && (
                            <div className="flex items-center gap-1 text-[#ffd108] font-bold text-sm mb-2">
                              <Clock className="h-3 w-3" />
                              {job.start_time}
                            </div>
                          )}

                          {/* Customer & Site */}
                          <h4 className="font-semibold text-sm truncate">
                            {customer?.company_name || 'Unknown Customer'}
                          </h4>
                          {location.address && (
                            <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {location.isOneOff && <span className="text-[#ffd108]">📍</span>}
                              {location.address}
                            </p>
                          )}

                          {/* Description */}
                          {job.description && (
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                              {job.description}
                            </p>
                          )}

                          {/* Equipment Type Badge */}
                          {job.equipment_type && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              {job.equipment_type}
                            </Badge>
                          )}

                          {/* Equipment & Operator Icons */}
                          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                            {/* Equipment Circle */}
                            <div 
                              className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                                eq 
                                  ? 'bg-[#ffd108] text-black' 
                                  : 'border-2 border-dashed border-muted-foreground hover:border-[#ffd108] hover:bg-[#ffd108]/10'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAllocationDialog(job);
                              }}
                              title={eq ? eq.asset_id : 'Click to allocate equipment'}
                            >
                              <Truck className={`h-4 w-4 ${eq ? '' : 'text-muted-foreground'}`} />
                            </div>

                            {/* Operator Circle */}
                            <div 
                              className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                                op 
                                  ? 'bg-[#ffd108] text-black' 
                                  : 'border-2 border-dashed border-muted-foreground hover:border-[#ffd108] hover:bg-[#ffd108]/10'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAllocationDialog(job);
                              }}
                              title={op ? op.name : 'Click to allocate operator'}
                            >
                              <Users className={`h-4 w-4 ${op ? '' : 'text-muted-foreground'}`} />
                            </div>

                            {/* Worker Type Circles */}
                            {job.worker_requirements?.length > 0 && (
                              <div className="flex items-center gap-1">
                                {job.worker_requirements.map((wr) => {
                                  const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
                                  const jobWorkerAllocs = getJobWorkerAllocations(job.id).filter(
                                    wa => wa.worker_type_id === wr.worker_type_id
                                  );
                                  const allocatedCount = jobWorkerAllocs.length;
                                  const isFullyAllocated = allocatedCount >= wr.quantity;
                                  const isPartiallyAllocated = allocatedCount > 0 && allocatedCount < wr.quantity;
                                  
                                  return (
                                    <div
                                      key={wr.worker_type_id}
                                      className={`w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-colors text-xs font-bold ${
                                        isFullyAllocated
                                          ? 'bg-green-500 text-white'
                                          : isPartiallyAllocated
                                          ? 'bg-amber-500 text-white'
                                          : 'border-2 border-dashed border-muted-foreground hover:border-[#ffd108] hover:bg-[#ffd108]/10'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAllocationDialog(job);
                                      }}
                                      title={`${workerType?.name || 'Worker'}: ${allocatedCount}/${wr.quantity} assigned`}
                                    >
                                      {wr.quantity}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Allocated Names */}
                            <div className="flex-1 min-w-0">
                              {eq || op ? (
                                <div className="text-xs">
                                  <div className="truncate">
                                    {eq && <span className="font-medium">{eq.asset_id}</span>}
                                    {eq && op && <span className="text-muted-foreground"> • </span>}
                                    {op && <span>{op.name}</span>}
                                  </div>
                                  {getAllocatedAttachmentsDisplay(job.id) && (
                                    <div className="text-muted-foreground truncate mt-0.5">
                                      {getAllocatedAttachmentsDisplay(job.id)}
                                    </div>
                                  )}
                                  {/* Show allocated workers */}
                                  {job.worker_requirements?.length > 0 && (
                                    <div className="text-muted-foreground truncate mt-0.5">
                                      {job.worker_requirements.map(wr => {
                                        const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
                                        const jobWorkerAllocs = getJobWorkerAllocations(job.id).filter(
                                          wa => wa.worker_type_id === wr.worker_type_id
                                        );
                                        const allocatedNames = jobWorkerAllocs
                                          .map(wa => getOperatorInfo(wa.operator_id)?.name)
                                          .filter(Boolean)
                                          .join(', ');
                                        
                                        if (allocatedNames) {
                                          return (
                                            <span key={wr.worker_type_id} className="block">
                                              {workerType?.name}: {allocatedNames}
                                            </span>
                                          );
                                        }
                                        return null;
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">
                                  Not allocated
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Add Job Button */}
                {status === 'not_allocated' && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full mt-3 border-2 border-dashed border-muted-foreground/30 hover:border-[#ffd108] hover:bg-[#ffd108]/10"
                    onClick={() => handleOpenJobDialog(null, currentDate)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Job
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* JOB SHEET (Flyout Panel) */}
      <Sheet open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading text-xl">
                {editingJob 
                  ? (editingJob.status === 'docketed' || editingJob.status === 'invoiced' ? 'View Job' : 'Edit Job')
                  : 'Create New Job'
                }
              </SheetTitle>
              {editingJob && getStatusBadge(editingJob.status)}
            </div>
            {editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced') && (
              <p className="text-sm text-muted-foreground mt-2">
                This job is {editingJob.status} and cannot be modified
              </p>
            )}
          </SheetHeader>
          
          {/* Presence indicator - show who else is viewing */}
          {editingJob && jobOthersViewing.length > 0 && (
            <PresenceIndicator othersViewing={jobOthersViewing} className="my-4" />
          )}
          
          {/* Version conflict alert */}
          {versionConflict && (
            <VersionConflictAlert 
              message={versionConflict.message}
              onRefresh={handleRefreshAndReload}
              className="my-4"
            />
          )}
          
          <form onSubmit={handleSubmitJob} className="space-y-4 py-4" data-testid="job-form">
            <div className="space-y-2">
              <Label className="label-overline">Customer</Label>
              <Select 
                value={jobForm.customer_id} 
                onValueChange={handleCustomerSelect}
                disabled={editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced')}
              >
                <SelectTrigger data-testid="job-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                    <span className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      ADD NEW CUSTOMER
                    </span>
                  </SelectItem>
                  {customers.length > 0 && <div className="border-t border-border my-1" />}
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        {c.company_name}
                        {c.requires_po && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 ml-1">PO</Badge>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contact Dropdown - Shows contacts for selected customer */}
            {jobForm.customer_id && (
              <div className="space-y-2">
                <Label className="label-overline">Contact</Label>
                <Select 
                  value={jobForm.contact_id} 
                  onValueChange={handleContactSelect}
                  disabled={editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced')}
                >
                  <SelectTrigger data-testid="job-contact-select">
                    <SelectValue placeholder="Select contact (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                      <span className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        ADD NEW CONTACT
                      </span>
                    </SelectItem>
                    {customerContacts.length > 0 && <div className="border-t border-border my-1" />}
                    {customerContacts.length === 0 && (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No contacts for this customer
                      </div>
                    )}
                    {customerContacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        <span className="flex items-center gap-2">
                          <User className="h-3 w-3 text-muted-foreground" />
                          {contact.name}
                          {contact.position && <span className="text-muted-foreground text-xs">({contact.position})</span>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* PO Number - Required if customer.requires_po is true */}
            {selectedCustomerRequiresPO && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  PO Number
                  <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Required</Badge>
                </Label>
                <Input
                  value={jobForm.po_number}
                  onChange={(e) => setJobForm({ ...jobForm, po_number: e.target.value })}
                  placeholder="Enter Purchase Order number"
                  required={selectedCustomerRequiresPO}
                  data-testid="job-po-number"
                />
              </div>
            )}

            {/* Optional PO Number for non-required customers */}
            {!selectedCustomerRequiresPO && jobForm.customer_id && (
              <div className="space-y-2">
                <Label className="label-overline">PO Number (Optional)</Label>
                <Input
                  value={jobForm.po_number}
                  onChange={(e) => setJobForm({ ...jobForm, po_number: e.target.value })}
                  placeholder="Enter Purchase Order number if applicable"
                  data-testid="job-po-number"
                />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-overline">Location</Label>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">One-off address</Label>
                  <Switch
                    checked={jobForm.use_one_off_address}
                    onCheckedChange={(checked) => setJobForm({ 
                      ...jobForm, 
                      use_one_off_address: checked,
                      site_id: checked ? '' : jobForm.site_id,
                      one_off_street: checked ? jobForm.one_off_street : '',
                      one_off_suburb: checked ? jobForm.one_off_suburb : '',
                      one_off_state: checked ? jobForm.one_off_state : '',
                      one_off_postcode: checked ? jobForm.one_off_postcode : ''
                    })}
                  />
                </div>
              </div>
              
              {jobForm.use_one_off_address ? (
                <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                  <div>
                    <Label className="text-xs text-muted-foreground">Search Address</Label>
                    <GooglePlacesAutocomplete
                      placeholder="Start typing an address..."
                      onPlaceSelect={(place) => {
                        setJobForm(prev => ({
                          ...prev,
                          one_off_street: place.street_address || '',
                          one_off_suburb: place.suburb || '',
                          one_off_state: place.state || '',
                          one_off_postcode: place.postcode || ''
                        }));
                      }}
                      data-testid="job-one-off-address-autocomplete"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Or enter manually below</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Street Address</Label>
                    <Input
                      value={jobForm.one_off_street}
                      onChange={(e) => setJobForm({ ...jobForm, one_off_street: e.target.value })}
                      placeholder="e.g., 123 Main Street"
                      data-testid="job-one-off-street"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Suburb</Label>
                      <Input
                        value={jobForm.one_off_suburb}
                        onChange={(e) => setJobForm({ ...jobForm, one_off_suburb: e.target.value })}
                        placeholder="Suburb"
                        data-testid="job-one-off-suburb"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">State</Label>
                      <Select 
                        value={jobForm.one_off_state} 
                        onValueChange={(v) => setJobForm({ ...jobForm, one_off_state: v })}
                      >
                        <SelectTrigger data-testid="job-one-off-state">
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {AUSTRALIAN_STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Post Code</Label>
                      <Input
                        value={jobForm.one_off_postcode}
                        onChange={(e) => setJobForm({ ...jobForm, one_off_postcode: e.target.value })}
                        placeholder="0000"
                        maxLength={4}
                        data-testid="job-one-off-postcode"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <Select value={jobForm.site_id} onValueChange={handleSiteSelect} disabled={!jobForm.customer_id}>
                  <SelectTrigger data-testid="job-site-select">
                    <SelectValue placeholder={jobForm.customer_id ? "Select site" : "Select customer first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {jobForm.customer_id && (
                      <>
                        <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                          <span className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            ADD NEW SITE
                          </span>
                        </SelectItem>
                        {getCustomerSites(jobForm.customer_id).length > 0 && <div className="border-t border-border my-1" />}
                      </>
                    )}
                    {getCustomerSites(jobForm.customer_id).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} - {formatSiteAddress(s)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Site Contact Selector - only show when a site is selected and has contacts */}
            {jobForm.site_id && !jobForm.use_one_off_address && siteContacts.length > 0 && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Site Contact
                </Label>
                <Select 
                  value={jobForm.site_contact_id || "__none__"} 
                  onValueChange={(value) => setJobForm({ ...jobForm, site_contact_id: value === "__none__" ? "" : value })}
                >
                  <SelectTrigger data-testid="job-site-contact-select">
                    <SelectValue placeholder="Select site contact (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No site contact</SelectItem>
                    {siteContacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        <span className="flex items-center gap-2">
                          {contact.name}
                          {contact.job_title && <span className="text-muted-foreground">({contact.job_title})</span>}
                          {contact.is_primary && <Badge variant="outline" className="text-xs ml-1">Primary</Badge>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {jobForm.site_contact_id && (
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    {(() => {
                      const contact = siteContacts.find(c => c.id === jobForm.site_contact_id);
                      if (!contact) return null;
                      return (
                        <>
                          {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
                          {contact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</span>}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="label-overline">Asset Type *</Label>
              <Select 
                value={jobForm.equipment_type} 
                onValueChange={(v) => {
                  // When equipment type changes, filter out incompatible attachment types
                  const newFilteredAttachments = getFilteredAttachmentTypes(v);
                  const validAttachmentIds = jobForm.attachment_type_ids.filter(id => 
                    newFilteredAttachments.some(at => at.id === id)
                  );
                  setJobForm({ 
                    ...jobForm, 
                    equipment_type: v,
                    attachment_type_ids: validAttachmentIds
                  });
                }} 
                required
              >
                <SelectTrigger data-testid="job-equipment-type-select">
                  <SelectValue placeholder="Select equipment type" />
                </SelectTrigger>
                <SelectContent>
                  {equipmentTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {getFilteredAttachmentTypes(jobForm.equipment_type).length > 0 && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  Attachment Types Required
                </Label>
                {!jobForm.equipment_type && (
                  <p className="text-xs text-amber-600 mb-2">
                    Select an equipment type first to see available attachments
                  </p>
                )}
                <div className="border border-border rounded-md p-3 max-h-32 overflow-y-auto space-y-2">
                  {getFilteredAttachmentTypes(jobForm.equipment_type).map((type) => (
                    <div key={type.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`job-att-type-${type.id}`}
                        checked={jobForm.attachment_type_ids.includes(type.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setJobForm({
                              ...jobForm,
                              attachment_type_ids: [...jobForm.attachment_type_ids, type.id]
                            });
                          } else {
                            setJobForm({
                              ...jobForm,
                              attachment_type_ids: jobForm.attachment_type_ids.filter(id => id !== type.id)
                            });
                          }
                        }}
                      />
                      <label htmlFor={`job-att-type-${type.id}`} className="text-sm cursor-pointer">
                        {type.name}
                      </label>
                    </div>
                  ))}
                </div>
                {jobForm.attachment_type_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {jobForm.attachment_type_ids.length} type(s) selected: {jobForm.attachment_type_ids.map(id => getAttachmentTypeName(id)).join(', ')}
                  </p>
                )}
              </div>
            )}

            {/* Worker Requirements Section */}
            {workerTypes.length > 0 && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Additional Workers Required
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select workers like Dogmen, Spotters, Riggers, etc.
                </p>
                <div className="border border-border rounded-md p-3 space-y-3">
                  {workerTypes.map((type) => {
                    const existing = jobForm.worker_requirements.find(wr => wr.worker_type_id === type.id);
                    return (
                      <div key={type.id} className="flex items-center gap-3">
                        <Checkbox
                          id={`job-worker-type-${type.id}`}
                          checked={!!existing}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setJobForm({
                                ...jobForm,
                                worker_requirements: [
                                  ...jobForm.worker_requirements,
                                  { worker_type_id: type.id, quantity: 1 }
                                ]
                              });
                            } else {
                              setJobForm({
                                ...jobForm,
                                worker_requirements: jobForm.worker_requirements.filter(
                                  wr => wr.worker_type_id !== type.id
                                )
                              });
                            }
                          }}
                        />
                        <label htmlFor={`job-worker-type-${type.id}`} className="text-sm cursor-pointer flex-1">
                          {type.name}
                          {type.description && (
                            <span className="text-xs text-muted-foreground ml-1">({type.description})</span>
                          )}
                        </label>
                        {existing && (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => {
                                if (existing.quantity > 1) {
                                  setJobForm({
                                    ...jobForm,
                                    worker_requirements: jobForm.worker_requirements.map(wr =>
                                      wr.worker_type_id === type.id
                                        ? { ...wr, quantity: wr.quantity - 1 }
                                        : wr
                                    )
                                  });
                                }
                              }}
                              disabled={existing.quantity <= 1}
                            >
                              -
                            </Button>
                            <span className="text-sm font-medium w-6 text-center">{existing.quantity}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => {
                                setJobForm({
                                  ...jobForm,
                                  worker_requirements: jobForm.worker_requirements.map(wr =>
                                    wr.worker_type_id === type.id
                                      ? { ...wr, quantity: wr.quantity + 1 }
                                      : wr
                                  )
                                });
                              }}
                            >
                              +
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {jobForm.worker_requirements.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {jobForm.worker_requirements.reduce((sum, wr) => sum + wr.quantity, 0)} worker(s) selected: {
                      jobForm.worker_requirements.map(wr => {
                        const wt = workerTypes.find(t => t.id === wr.worker_type_id);
                        return `${wr.quantity}x ${wt?.name || 'Unknown'}`;
                      }).join(', ')
                    }
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-overline">Start Date</Label>
                  <DatePicker
                    value={jobForm.start_date}
                    onChange={(date) => setJobForm({ ...jobForm, start_date: date })}
                    formatDisplay={formatDateDisplay}
                    className="w-full"
                    testId="job-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">End Date</Label>
                  <DatePicker
                    value={jobForm.end_date}
                    onChange={(date) => setJobForm({ ...jobForm, end_date: date })}
                    formatDisplay={formatDateDisplay}
                    minDate={jobForm.start_date}
                    className="w-full"
                    testId="job-end-date"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-overline">Start Time</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={jobForm.start_time?.split(':')[0] || '07'} 
                      onValueChange={(hour) => {
                        const currentMinute = jobForm.start_time?.split(':')[1] || '00';
                        setJobForm({ ...jobForm, start_time: `${hour}:${currentMinute}` });
                      }}
                    >
                      <SelectTrigger className="w-20 font-mono">
                        <SelectValue placeholder="HH" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(hour => (
                          <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="flex items-center text-lg font-bold">:</span>
                    <Select 
                      value={jobForm.start_time?.split(':')[1] || '00'} 
                      onValueChange={(minute) => {
                        const currentHour = jobForm.start_time?.split(':')[0] || '07';
                        setJobForm({ ...jobForm, start_time: `${currentHour}:${minute}` });
                      }}
                    >
                      <SelectTrigger className="w-20 font-mono">
                        <SelectValue placeholder="MM" />
                      </SelectTrigger>
                      <SelectContent>
                        {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(minute => (
                          <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Est. Duration</Label>
                  <Select 
                    value={jobForm.est_duration?.toString() || 'none'} 
                    onValueChange={(value) => setJobForm({ ...jobForm, est_duration: value === 'none' ? '' : parseFloat(value) })}
                  >
                    <SelectTrigger className="font-mono">
                      <SelectValue placeholder="Select hours" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12].map(hours => (
                        <SelectItem key={hours} value={hours.toString()}>{hours} {hours === 1 ? 'hour' : 'hours'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Calculated Est. End Time */}
                {jobForm.start_time && jobForm.est_duration && (
                  <div className="space-y-2">
                    <Label className="label-overline">Est. End Time</Label>
                    <div className="h-10 px-3 py-2 rounded-md border border-input bg-muted/50 font-mono text-sm flex items-center">
                      {(() => {
                        const [hours, minutes] = jobForm.start_time.split(':').map(Number);
                        const durationHours = Math.floor(jobForm.est_duration);
                        const durationMinutes = (jobForm.est_duration % 1) * 60;
                        let endHours = hours + durationHours;
                        let endMinutes = minutes + durationMinutes;
                        if (endMinutes >= 60) {
                          endHours += 1;
                          endMinutes -= 60;
                        }
                        const nextDay = endHours >= 24;
                        if (nextDay) endHours -= 24;
                        return (
                          <span className="flex items-center gap-2">
                            {endHours.toString().padStart(2, '0')}:{endMinutes.toString().padStart(2, '0')}
                            {nextDay && <span className="text-xs text-amber-600 font-medium">(+1 day)</span>}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Description</Label>
              <Textarea
                value={jobForm.description}
                onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                placeholder="Job description..."
                rows={2}
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea
                value={jobForm.notes}
                onChange={(e) => setJobForm({ ...jobForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Tags/Labels with Autocomplete */}
            <div className="space-y-2">
              <Label className="label-overline">Tags / Labels</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      onFocus={() => setTagInput(tagInput)} // Trigger re-render to show suggestions
                      placeholder="Type to search or add new tag..."
                      disabled={editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced')}
                      list="tag-suggestions"
                    />
                    {/* Datalist for native browser autocomplete */}
                    <datalist id="tag-suggestions">
                      {availableTags
                        .filter(tag => !jobForm.tags.includes(tag))
                        .map((tag, index) => (
                          <option key={index} value={tag} />
                        ))
                      }
                    </datalist>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim() || (editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced'))}
                    title="Add new tag"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {/* Show popular tags as quick-add buttons */}
                {availableTags.length > 0 && jobForm.tags.length === 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Popular tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {availableTags.slice(0, 5).map((tag, index) => (
                        <Button
                          key={index}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setJobForm({ ...jobForm, tags: [...jobForm.tags, tag] });
                          }}
                          className="h-7 text-xs"
                        >
                          + {tag}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {jobForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {jobForm.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="pl-2 pr-1 py-1">
                      {tag}
                      {(!editingJob || (editingJob.status !== 'docketed' && editingJob.status !== 'invoiced')) && (
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:bg-muted rounded-full p-0.5"
                        >
                          <XCircle className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => setJobDialogOpen(false)} onMouseDown={() => document.activeElement?.blur()}>
                {editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced') ? 'Close' : 'Cancel'}
              </Button>
              {(!editingJob || (editingJob.status !== 'docketed' && editingJob.status !== 'invoiced')) && (
                <Button type="submit" className="btn-volt" data-testid="save-job-btn" onMouseDown={() => document.activeElement?.blur()}>{editingJob ? 'Update' : 'Create'} Job</Button>
              )}
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* MULTI-DAY ALLOCATION MANAGEMENT DIALOG */}
      <Dialog open={multiDayDrawerOpen} onOpenChange={(open) => {
        setMultiDayDrawerOpen(open);
        if (!open) {
          setEditingMultiDayAllocation(null);
          setMultiDayJob(null);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-volt" />
              Multi-Day Job Allocations
            </DialogTitle>
          </DialogHeader>
          
          {multiDayJob && (
            <div className="space-y-4 mt-4">
              {/* Job Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{getCustomerInfo(multiDayJob.customer_id)?.company_name}</p>
                <p className="text-sm text-muted-foreground">{multiDayJob.description}</p>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <Calendar className="h-4 w-4 text-volt" />
                  <span>{formatDateDisplay(multiDayJob.start_date)} - {formatDateDisplay(multiDayJob.end_date)}</span>
                </div>
              </div>
              
              {/* Coverage Stats */}
              {multiDayStats && (
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Coverage</span>
                    <span className="text-sm">
                      <span className="text-green-600">{multiDayStats.confirmed_days}</span>
                      {multiDayStats.draft_days > 0 && (
                        <span className="text-amber-600"> + {multiDayStats.draft_days} draft</span>
                      )}
                      <span className="text-muted-foreground"> / {multiDayStats.total_days} days</span>
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div className="h-full flex">
                      <div 
                        className="bg-green-500 h-full" 
                        style={{ width: `${(multiDayStats.confirmed_days / multiDayStats.total_days) * 100}%` }}
                      />
                      <div 
                        className="bg-amber-500 h-full" 
                        style={{ width: `${(multiDayStats.draft_days / multiDayStats.total_days) * 100}%` }}
                      />
                    </div>
                  </div>
                  {multiDayStats.next_unallocated_date && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Next unallocated: {formatDateDisplay(multiDayStats.next_unallocated_date)}
                    </p>
                  )}
                </div>
              )}
              
              {/* Allocations List */}
              {!editingMultiDayAllocation ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Allocations</h3>
                    <Button size="sm" onClick={handleAddMultiDayAllocation} className="btn-volt" onMouseDown={() => document.activeElement?.blur()}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Allocation
                    </Button>
                  </div>
                  
                  {loadingMultiDay ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : multiDayAllocations.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg border-dashed">
                      <Calendar className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No allocations yet</p>
                      <Button size="sm" variant="outline" className="mt-2" onClick={handleAddMultiDayAllocation}>
                        Add First Allocation
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {multiDayAllocations.map((alloc) => (
                        <div 
                          key={alloc.id} 
                          className={`border rounded-lg p-3 ${
                            alloc.status === 'draft' 
                              ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' 
                              : 'border-green-500 bg-green-50 dark:bg-green-950/20'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={alloc.status === 'draft' ? 'warning' : 'success'}>
                                  {alloc.status === 'draft' ? '🟡 DRAFT' : '🟢 CONFIRMED'}
                                </Badge>
                                <span className="text-sm font-medium">
                                  {formatDateDisplay(alloc.start_date)}
                                  {alloc.end_date && alloc.end_date !== alloc.start_date && (
                                    <> - {formatDateDisplay(alloc.end_date)}</>
                                  )}
                                </span>
                              </div>
                              <div className="text-sm space-y-0.5">
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3 text-muted-foreground" />
                                  {alloc.operator_name} ({alloc.operator_code})
                                </div>
                                <div className="flex items-center gap-1">
                                  <Truck className="h-3 w-3 text-muted-foreground" />
                                  {alloc.equipment_name} ({alloc.equipment_asset_id})
                                </div>
                                {alloc.worker_allocations?.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Users className="h-3 w-3 text-muted-foreground" />
                                    {alloc.worker_allocations.map(w => 
                                      `${w.worker_type_name}${w.operator_name ? ` - ${w.operator_name}` : ''}`
                                    ).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditMultiDayAllocation(alloc)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                {alloc.status === 'draft' ? (
                                  <DropdownMenuItem onClick={() => handleConfirmAllocation(alloc.id)}>
                                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                                    Confirm
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleRevertToDraft(alloc.id)}>
                                    <XCircle className="h-4 w-4 mr-2 text-amber-600" />
                                    Revert to Draft
                                  </DropdownMenuItem>
                                )}
                                {multiDayStats?.next_unallocated_date && (
                                  <DropdownMenuItem onClick={() => handleCopyAllocation(alloc.id)}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copy to Next Period
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteMultiDayAllocation(alloc.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Unallocated period notice */}
                  {multiDayStats && multiDayStats.unallocated_days > 0 && (
                    <div className="border border-dashed border-amber-500 rounded-lg p-3 bg-amber-50 dark:bg-amber-950/20">
                      <div className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {multiDayStats.unallocated_days} day{multiDayStats.unallocated_days !== 1 ? 's' : ''} unallocated
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-2 border-amber-500 text-amber-600 hover:bg-amber-100"
                        onClick={handleAddMultiDayAllocation}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Allocation
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                /* Allocation Edit Form */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">
                      {editingMultiDayAllocation === 'new' ? 'Add Allocation' : 'Edit Allocation'}
                    </h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditingMultiDayAllocation(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Start Date *</Label>
                      <DatePicker
                        value={multiDayAllocationForm.start_date}
                        onChange={(date) => setMultiDayAllocationForm(prev => ({ 
                          ...prev, 
                          start_date: date 
                        }))}
                        formatDisplay={formatDateDisplay}
                        minDate={multiDayJob?.start_date}
                        maxDate={multiDayJob?.end_date}
                        className="w-full"
                        testId="multi-day-alloc-start-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>End Date</Label>
                      <DatePicker
                        value={multiDayAllocationForm.end_date}
                        onChange={(date) => setMultiDayAllocationForm(prev => ({ 
                          ...prev, 
                          end_date: date 
                        }))}
                        formatDisplay={formatDateDisplay}
                        minDate={multiDayAllocationForm.start_date || multiDayJob?.start_date}
                        maxDate={multiDayJob?.end_date}
                        className="w-full"
                        testId="multi-day-alloc-end-date"
                      />
                      <p className="text-xs text-muted-foreground">Leave blank for single day</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Operator *</Label>
                    <Select
                      value={multiDayAllocationForm.operator_id}
                      onValueChange={(value) => setMultiDayAllocationForm(prev => ({ 
                        ...prev, 
                        operator_id: value 
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select operator" />
                      </SelectTrigger>
                      <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                        {operators.map(op => (
                          <SelectItem key={op.id} value={op.id}>
                            {op.name} ({op.operator_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {!multiDayAllocationForm.is_combo_allocation && (
                    <div>
                      <Label>Asset *</Label>
                      {multiDayJob?.equipment_type && (
                        <p className="text-xs text-muted-foreground mb-1">
                          Required type: <span className="font-medium text-foreground">{multiDayJob.equipment_type}</span>
                        </p>
                      )}
                      <Select
                        value={multiDayAllocationForm.equipment_id}
                        onValueChange={(value) => setMultiDayAllocationForm(prev => ({ 
                          ...prev, 
                          equipment_id: value 
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select equipment" />
                        </SelectTrigger>
                        <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                          {(() => {
                            const jobEquipmentType = multiDayJob?.equipment_type;
                            
                            // If no equipment type specified, show all
                            if (!jobEquipmentType) {
                              return equipment.map((eq) => (
                                <SelectItem key={eq.id} value={eq.id}>
                                  {eq.asset_id} {eq.equipment_type && `(${eq.equipment_type})`}
                                </SelectItem>
                              ));
                            }
                            
                            // Split equipment into matching and other types
                            const matchingEquipment = equipment.filter(eq => eq.equipment_type === jobEquipmentType);
                            const otherEquipment = equipment.filter(eq => eq.equipment_type !== jobEquipmentType);
                            
                            return (
                              <>
                                {/* Matching Equipment Group */}
                                {matchingEquipment.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-xs font-semibold text-[#ffd108] flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" />
                                      {jobEquipmentType} (Recommended)
                                    </SelectLabel>
                                    {matchingEquipment.map((eq) => (
                                      <SelectItem key={eq.id} value={eq.id} className="pl-4">
                                        <span className="flex items-center gap-2">
                                          <span className="font-medium">{eq.asset_id}</span>
                                          {eq.name && <span className="text-muted-foreground text-xs">- {eq.name}</span>}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                )}
                                
                                {/* Other Equipment Group */}
                                {otherEquipment.length > 0 && (
                                  <>
                                    {matchingEquipment.length > 0 && <SelectSeparator />}
                                    <SelectGroup>
                                      <SelectLabel className="text-xs text-muted-foreground">
                                        Other Equipment
                                      </SelectLabel>
                                      {otherEquipment.map((eq) => (
                                        <SelectItem key={eq.id} value={eq.id} className="pl-4">
                                          <span className="flex items-center gap-2">
                                            <span>{eq.asset_id}</span>
                                            <span className="text-muted-foreground text-xs">({eq.equipment_type || 'Unknown type'})</span>
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </>
                                )}
                                
                                {/* No matching equipment warning */}
                                {matchingEquipment.length === 0 && (
                                  <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded">
                                    No {jobEquipmentType} equipment available
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Combo Equipment Selection */}
                  {multiDayAllocationForm.is_combo_allocation && (
                    <div className="space-y-3">
                      <Label>Combo Assets</Label>
                      {multiDayAllocationForm.combo_equipment_ids.map((comp, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-sm min-w-[100px]">{comp.component_type_name}:</span>
                          <Select
                            value={comp.equipment_id}
                            onValueChange={(value) => {
                              const updated = [...multiDayAllocationForm.combo_equipment_ids];
                              updated[idx] = { ...updated[idx], equipment_id: value };
                              setMultiDayAllocationForm(prev => ({ 
                                ...prev, 
                                combo_equipment_ids: updated 
                              }));
                            }}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select equipment" />
                            </SelectTrigger>
                            <SelectContent>
                              {equipment
                                .filter(eq => eq.equipment_type === comp.component_type_name)
                                .map(eq => (
                                  <SelectItem key={eq.id} value={eq.id}>
                                    {eq.asset_id}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Worker Allocations */}
                  {multiDayAllocationForm.worker_allocations.length > 0 && (
                    <div className="space-y-2">
                      <Label>Additional Workers</Label>
                      {multiDayAllocationForm.worker_allocations.map((wa, idx) => {
                        const workerType = workerTypes.find(wt => wt.id === wa.worker_type_id);
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-sm min-w-[80px]">{workerType?.name || 'Worker'}:</span>
                            <Select
                              value={wa.operator_id || '__none__'}
                              onValueChange={(value) => {
                                const updated = [...multiDayAllocationForm.worker_allocations];
                                updated[idx] = { ...updated[idx], operator_id: value === '__none__' ? '' : value };
                                setMultiDayAllocationForm(prev => ({ 
                                  ...prev, 
                                  worker_allocations: updated 
                                }));
                              }}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Select worker" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Not assigned</SelectItem>
                                {operators.map(op => (
                                  <SelectItem key={op.id} value={op.id}>
                                    {op.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Status Selection */}
                  <div className="border rounded-lg p-3 space-y-2">
                    <Label>Save as:</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="allocation_status"
                          value="draft"
                          checked={multiDayAllocationForm.status === 'draft'}
                          onChange={() => setMultiDayAllocationForm(prev => ({ ...prev, status: 'draft' }))}
                          className="text-amber-500"
                        />
                        <span className="text-sm">
                          <span className="font-medium">Draft</span>
                          <span className="text-muted-foreground"> - operator will not see</span>
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="allocation_status"
                          value="confirmed"
                          checked={multiDayAllocationForm.status === 'confirmed'}
                          onChange={() => setMultiDayAllocationForm(prev => ({ ...prev, status: 'confirmed' }))}
                          className="text-green-500"
                        />
                        <span className="text-sm">
                          <span className="font-medium">Confirmed</span>
                          <span className="text-muted-foreground"> - operator will see</span>
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setEditingMultiDayAllocation(null)}
                      onMouseDown={() => document.activeElement?.blur()}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="flex-1 btn-volt"
                      onClick={handleSaveMultiDayAllocation}
                      disabled={!multiDayAllocationForm.operator_id || !multiDayAllocationForm.start_date}
                      onMouseDown={() => document.activeElement?.blur()}
                    >
                      {editingMultiDayAllocation === 'new' ? 'Add Allocation' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ALLOCATION DIALOG */}
      <Dialog open={allocationDialogOpen} onOpenChange={setAllocationDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedJobForAllocation?.status === 'docketed' || selectedJobForAllocation?.status === 'invoiced' 
                ? 'View Allocation' 
                : 'Allocate Resources'}
            </DialogTitle>
          </DialogHeader>
          {selectedJobForAllocation && (
            <div className="space-y-4">
              {/* Read-only notice for docketed/invoiced jobs */}
              {(selectedJobForAllocation.status === 'docketed' || selectedJobForAllocation.status === 'invoiced') && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    This job has been docketed. Allocations cannot be modified to maintain data accuracy.
                  </p>
                </div>
              )}
              <div className="p-3 bg-muted rounded-sm">
                <p className="text-sm">
                  <span className="font-medium">{getCustomerInfo(selectedJobForAllocation.customer_id)?.company_name}</span>
                  <br />
                  <span className="text-muted-foreground">{selectedJobForAllocation.description}</span>
                </p>
              </div>

              {/* Smart Operator Suggestions - Only show if AI is enabled */}
              {isAiAvailable() && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowSuggestions(!showSuggestions)}
                  className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-[#ffd108]/10 to-transparent hover:from-[#ffd108]/20 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#ffd108]" />
                    <span className="font-heading font-semibold text-sm">Smart Operator Suggestions</span>
                    {operatorSuggestions.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {operatorSuggestions.filter(s => s.meets_requirements && s.is_available).length} qualified
                      </Badge>
                    )}
                  </div>
                  {showSuggestions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                
                {showSuggestions && (
                  <div className="p-3 border-t bg-muted/30">
                    {suggestionsLoading ? (
                      <div className="text-center py-4">
                        <div className="animate-spin h-6 w-6 border-2 border-[#ffd108] border-t-transparent rounded-full mx-auto"></div>
                        <p className="text-sm text-muted-foreground mt-2">Analyzing operators...</p>
                      </div>
                    ) : operatorSuggestions.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No operator suggestions available
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {operatorSuggestions.slice(0, 6).map((suggestion, idx) => (
                          <div 
                            key={suggestion.operator_id}
                            onClick={() => selectSuggestedOperator(suggestion.operator_id)}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              allocationForm.operator_id === suggestion.operator_id
                                ? 'border-[#ffd108] bg-[#ffd108]/10'
                                : suggestion.meets_requirements && suggestion.is_available
                                  ? 'border-green-500/30 hover:border-green-500 hover:bg-green-500/5'
                                  : 'border-border opacity-60 hover:opacity-100'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3">
                                {/* Rank Circle */}
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  idx === 0 ? 'bg-[#ffd108] text-black' :
                                  idx === 1 ? 'bg-gray-400 text-black' :
                                  idx === 2 ? 'bg-amber-700 text-white' :
                                  'bg-muted text-muted-foreground'
                                }`}>
                                  {idx + 1}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{suggestion.operator_name}</span>
                                    {suggestion.is_preferred && (
                                      <Star className="h-3 w-3 text-[#ffd108] fill-[#ffd108]" />
                                    )}
                                  </div>
                                  {suggestion.operator_suburb && (
                                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      {suggestion.operator_suburb} {suggestion.operator_postcode}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-[#ffd108]">{suggestion.score}</div>
                                <div className="text-xs text-muted-foreground">score</div>
                              </div>
                            </div>
                            
                            {/* Status Indicators */}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {suggestion.is_available ? (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Available
                                </Badge>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-400 text-xs">
                                  <XCircle className="h-3 w-3 mr-1" />Busy
                                </Badge>
                              )}
                              {suggestion.meets_requirements ? (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Qualified
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />Missing Req
                                </Badge>
                              )}
                              {suggestion.is_preferred && (
                                <Badge className="bg-[#ffd108]/20 text-[#ffd108] text-xs">
                                  <Star className="h-3 w-3 mr-1" />Preferred #{suggestion.preference_priority || '-'}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Reasons */}
                            {(suggestion.reasons.length > 0 || suggestion.warnings.length > 0) && (
                              <div className="mt-2 text-xs space-y-1">
                                {suggestion.reasons.slice(0, 3).map((reason, i) => (
                                  <div key={i} className="text-green-400 flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" />{reason}
                                  </div>
                                ))}
                                {suggestion.warnings.slice(0, 2).map((warning, i) => (
                                  <div key={i} className="text-amber-400 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" />{warning}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              <div className="space-y-2">
                <Label className="label-overline">Asset</Label>
                
                {/* Regular Equipment Selection (non-combo) */}
                {!allocationForm.is_combo_allocation && (
                  <>
                    {/* Show job's required equipment type if set */}
                    {selectedJobForAllocation?.equipment_type && (
                      <p className="text-xs text-muted-foreground mb-1">
                        Required type: <span className="font-medium text-foreground">{selectedJobForAllocation.equipment_type}</span>
                      </p>
                    )}
                    <Select value={allocationForm.equipment_id} onValueChange={(v) => setAllocationForm({ ...allocationForm, equipment_id: v })} onOpenChange={(open) => !open && document.activeElement?.blur()}>
                      <SelectTrigger><SelectValue placeholder="Select equipment" /></SelectTrigger>
                      <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                        {(() => {
                          const equipmentList = overrideCheck ? equipment : availableEquipment;
                          const jobEquipmentType = selectedJobForAllocation?.equipment_type;
                          
                          // If no equipment type specified on job, show flat list
                          if (!jobEquipmentType) {
                            return equipmentList.map((eq) => (
                              <SelectItem key={eq.id} value={eq.id}>
                                {eq.asset_id} {eq.equipment_type && `(${eq.equipment_type})`}
                                {overrideCheck && eq.status !== 'available' && (
                                  <span className="ml-2 text-xs text-amber-500">({eq.status})</span>
                                )}
                              </SelectItem>
                            ));
                          }
                          
                          // Split equipment into matching and other types
                          const matchingEquipment = equipmentList.filter(eq => eq.equipment_type === jobEquipmentType);
                          const otherEquipment = equipmentList.filter(eq => eq.equipment_type !== jobEquipmentType);
                          
                          return (
                            <>
                              {/* Matching Equipment Group */}
                              {matchingEquipment.length > 0 && (
                                <SelectGroup>
                                  <SelectLabel className="text-xs font-semibold text-[#ffd108] flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {jobEquipmentType} (Recommended)
                                  </SelectLabel>
                                  {matchingEquipment.map((eq) => (
                                    <SelectItem key={eq.id} value={eq.id} className="pl-4">
                                      <span className="flex items-center gap-2">
                                        <span className="font-medium">{eq.asset_id}</span>
                                        {eq.name && <span className="text-muted-foreground text-xs">- {eq.name}</span>}
                                        {overrideCheck && eq.status !== 'available' && (
                                          <span className="text-xs text-amber-500">({eq.status})</span>
                                        )}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              )}
                              
                              {/* Other Equipment Group */}
                              {otherEquipment.length > 0 && (
                                <>
                                  {matchingEquipment.length > 0 && <SelectSeparator />}
                                  <SelectGroup>
                                    <SelectLabel className="text-xs text-muted-foreground">
                                      Other Equipment
                                    </SelectLabel>
                                    {otherEquipment.map((eq) => (
                                      <SelectItem key={eq.id} value={eq.id} className="pl-4">
                                        <span className="flex items-center gap-2">
                                          <span>{eq.asset_id}</span>
                                          <span className="text-muted-foreground text-xs">({eq.equipment_type || 'Unknown type'})</span>
                                          {overrideCheck && eq.status !== 'available' && (
                                            <span className="text-xs text-amber-500">({eq.status})</span>
                                          )}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </>
                              )}
                              
                              {/* No matching equipment warning */}
                              {matchingEquipment.length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded">
                                  No {jobEquipmentType} equipment available
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                    {!overrideCheck && availableEquipment.length === 0 && (
                      <p className="text-xs text-[#FF4F00] flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />No available equipment
                      </p>
                    )}
                  </>
                )}
                
                {/* Combo Equipment Selection */}
                {allocationForm.is_combo_allocation && (
                  <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-purple-700 dark:text-purple-300">
                      <Truck className="h-4 w-4" />
                      Combo Equipment - Allocate one machine for each component
                    </div>
                    
                    {allocationForm.combo_equipment_ids?.length > 0 ? (
                      allocationForm.combo_equipment_ids.map((comboItem, index) => {
                        // Skip rendering if comboItem is invalid
                        if (!comboItem?.component_type_id) return null;
                        
                        // Get available equipment for this component type
                        const componentEquipment = (overrideCheck ? equipment : availableEquipment)
                          .filter(eq => {
                            const eqType = equipmentTypesWithIds.find(t => t.name === eq.equipment_type);
                            return eqType?.id === comboItem.component_type_id;
                          });
                        
                        // Also exclude already selected equipment in other combo slots
                        const selectedInOtherSlots = allocationForm.combo_equipment_ids
                          .filter((_, i) => i !== index)
                          .map(c => c?.equipment_id)
                          .filter(Boolean);
                        
                        const filteredComponentEquipment = componentEquipment.filter(
                          eq => !selectedInOtherSlots.includes(eq.id) || eq.id === comboItem.equipment_id
                        );
                        
                        return (
                          <div key={comboItem.component_type_id || index} className="space-y-1">
                            <Label className="text-xs text-purple-600 dark:text-purple-400">
                              {comboItem.component_type_name || 'Unknown Component'} *
                            </Label>
                            <Select 
                              value={comboItem.equipment_id || ''} 
                              onValueChange={(v) => {
                                const newComboEquipIds = [...allocationForm.combo_equipment_ids];
                                newComboEquipIds[index] = { ...comboItem, equipment_id: v };
                                setAllocationForm({ ...allocationForm, combo_equipment_ids: newComboEquipIds });
                              }}
                            >
                              <SelectTrigger className="bg-white dark:bg-slate-800">
                                <SelectValue placeholder={`Select ${comboItem.component_type_name || 'equipment'}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {filteredComponentEquipment.map((eq) => (
                                  <SelectItem key={eq.id} value={eq.id}>
                                    {eq.asset_id}
                                  </SelectItem>
                                ))}
                                {filteredComponentEquipment.length === 0 && (
                                  <div className="p-2 text-xs text-muted-foreground">
                                    No available {comboItem.component_type_name || ''} equipment
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-xs text-amber-600">
                        No combo components configured for this equipment type
                      </p>
                    )}
                    
                    {allocationForm.combo_equipment_ids?.some(c => c && !c.equipment_id) && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Please select equipment for all combo components
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Attachments Selection - Now between Equipment and Operator */}
              {(() => {
                const jobAttachmentTypeIds = selectedJobForAllocation?.attachment_type_ids || [];
                const filteredAttachments = attachments.filter(a => 
                  (a.status === 'available' || allocationForm.attachment_ids.includes(a.id)) &&
                  (jobAttachmentTypeIds.length === 0 || jobAttachmentTypeIds.includes(a.attachment_type_id))
                );
                
                if (filteredAttachments.length === 0 && jobAttachmentTypeIds.length === 0) return null;
                
                return (
                  <div className="space-y-2">
                    <Label className="label-overline flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Attachments
                      {jobAttachmentTypeIds.length > 0 && (
                        <span className="text-xs text-muted-foreground font-normal">
                          (Required: {jobAttachmentTypeIds.map(id => getAttachmentTypeName(id)).join(', ')})
                        </span>
                      )}
                    </Label>
                    {filteredAttachments.length > 0 ? (
                      <div className="border border-border rounded-md p-3 max-h-32 overflow-y-auto space-y-2">
                        {filteredAttachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`attachment-${attachment.id}`}
                              checked={allocationForm.attachment_ids.includes(attachment.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setAllocationForm({
                                    ...allocationForm,
                                    attachment_ids: [...allocationForm.attachment_ids, attachment.id]
                                  });
                                } else {
                                  setAllocationForm({
                                    ...allocationForm,
                                    attachment_ids: allocationForm.attachment_ids.filter(id => id !== attachment.id)
                                  });
                                }
                              }}
                            />
                            <label
                              htmlFor={`attachment-${attachment.id}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {attachment.name}
                              {attachment.size && <span className="text-muted-foreground ml-1">({attachment.size})</span>}
                            </label>
                            <Badge variant="outline" className="text-xs">{getAttachmentTypeName(attachment.attachment_type_id)}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No attachments available for required types</p>
                    )}
                  </div>
                );
              })()}

              <div className="space-y-2">
                <Label className="label-overline">Operator</Label>
                <Select value={allocationForm.operator_id} onValueChange={(v) => setAllocationForm({ ...allocationForm, operator_id: v })} onOpenChange={(open) => !open && document.activeElement?.blur()}>
                  <SelectTrigger><SelectValue placeholder="Select operator" /></SelectTrigger>
                  <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                    {(overrideCheck ? operators : availableOperators).map((op) => {
                      const conflicts = getOperatorConflicts(
                        op.id, 
                        allocationForm.start_date, 
                        allocationForm.end_date,
                        selectedJobForAllocation?.id
                      );
                      return (
                        <SelectItem key={op.id} value={op.id}>
                          <div className="flex items-center justify-between w-full gap-2">
                            <span>{op.name}</span>
                            <div className="flex items-center gap-1">
                              {conflicts.length > 0 && (
                                <span className="text-xs text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                  Allocated ({conflicts[0].customer_name})
                                </span>
                              )}
                              {overrideCheck && !op.is_available && (
                                <span className="text-xs text-amber-500">(unavailable)</span>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {!overrideCheck && availableOperators.length === 0 && (
                  <p className="text-xs text-[#FF4F00] flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />No available operators
                  </p>
                )}
              </div>

              {/* Additional Workers Section */}
              {selectedJobForAllocation?.worker_requirements?.length > 0 && (
                <div className="space-y-3 p-3 bg-muted/50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#ffd108]" />
                    <Label className="label-overline">Additional Workers Required</Label>
                  </div>
                  <div className="space-y-3">
                    {selectedJobForAllocation.worker_requirements.map((wr, idx) => {
                      const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
                      // For each required quantity, show a dropdown
                      return Array.from({ length: wr.quantity }).map((_, qIdx) => {
                        const allocationKey = `${wr.worker_type_id}_${qIdx}`;
                        const existingAlloc = allocationForm.worker_allocations.find(
                          (wa, waIdx) => wa.worker_type_id === wr.worker_type_id && 
                          allocationForm.worker_allocations.filter((w, i) => w.worker_type_id === wr.worker_type_id && i < waIdx).length === qIdx
                        );
                        const currentOperatorId = existingAlloc?.operator_id || 'unassigned';
                        
                        return (
                          <div key={allocationKey} className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-28">
                              <Badge variant="outline" className="text-xs">
                                {workerType?.name || 'Unknown'} #{qIdx + 1}
                              </Badge>
                            </div>
                            <Select
                              value={currentOperatorId}
                              onValueChange={(v) => {
                                const actualValue = v === 'unassigned' ? '' : v;
                                const newWorkerAllocations = [...allocationForm.worker_allocations];
                                // Find or create the allocation entry
                                let found = false;
                                let count = 0;
                                for (let i = 0; i < newWorkerAllocations.length; i++) {
                                  if (newWorkerAllocations[i].worker_type_id === wr.worker_type_id) {
                                    if (count === qIdx) {
                                      newWorkerAllocations[i] = { ...newWorkerAllocations[i], operator_id: actualValue };
                                      found = true;
                                      break;
                                    }
                                    count++;
                                  }
                                }
                                if (!found) {
                                  newWorkerAllocations.push({
                                    worker_type_id: wr.worker_type_id,
                                    operator_id: actualValue
                                  });
                                }
                                setAllocationForm({ ...allocationForm, worker_allocations: newWorkerAllocations });
                              }}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Select operator" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned">-- Not assigned --</SelectItem>
                                {operators
                                  .filter(op => op.id !== allocationForm.operator_id) // Exclude main operator
                                  .map((op) => {
                                    const conflicts = getOperatorConflicts(
                                      op.id, 
                                      allocationForm.start_date, 
                                      allocationForm.end_date,
                                      selectedJobForAllocation?.id
                                    );
                                    return (
                                      <SelectItem key={op.id} value={op.id}>
                                        <div className="flex items-center justify-between w-full gap-2">
                                          <span>{op.name}</span>
                                          {conflicts.length > 0 && (
                                            <span className="text-xs text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                              Allocated ({conflicts[0].customer_name})
                                            </span>
                                          )}
                                        </div>
                                      </SelectItem>
                                    );
                                  })}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      });
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Assign operators to additional worker roles for this job
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Start Date</Label>
                  {/* Single-day job: read-only, Multi-day job: editable */}
                  {(!selectedJobForAllocation?.end_date || selectedJobForAllocation?.end_date === selectedJobForAllocation?.start_date) ? (
                    <div className="flex items-center h-9 px-3 border border-input rounded-md bg-muted/50">
                      <span className="font-mono text-sm">{formatDateDisplay(allocationForm.start_date)}</span>
                    </div>
                  ) : (
                    <DatePicker
                      value={allocationForm.start_date}
                      onChange={(date) => setAllocationForm({ ...allocationForm, start_date: date })}
                      formatDisplay={formatDateDisplay}
                      minDate={selectedJobForAllocation?.start_date}
                      maxDate={selectedJobForAllocation?.end_date}
                      className="w-full"
                      testId="allocation-start-date"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">End Date</Label>
                  {/* Single-day job: read-only, Multi-day job: editable */}
                  {(!selectedJobForAllocation?.end_date || selectedJobForAllocation?.end_date === selectedJobForAllocation?.start_date) ? (
                    <div className="flex items-center h-9 px-3 border border-input rounded-md bg-muted/50">
                      <span className="font-mono text-sm">{allocationForm.end_date ? formatDateDisplay(allocationForm.end_date) : '-'}</span>
                    </div>
                  ) : (
                    <DatePicker
                      value={allocationForm.end_date}
                      onChange={(date) => setAllocationForm({ ...allocationForm, end_date: date })}
                      formatDisplay={formatDateDisplay}
                      minDate={allocationForm.start_date || selectedJobForAllocation?.start_date}
                      maxDate={selectedJobForAllocation?.end_date}
                      className="w-full"
                      testId="allocation-end-date"
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <Label htmlFor="override-check" className="text-sm font-medium cursor-pointer">
                    Override Check
                  </Label>
                </div>
                <Switch
                  id="override-check"
                  checked={overrideCheck}
                  onCheckedChange={setOverrideCheck}
                  data-testid="override-check-switch"
                />
              </div>
              {overrideCheck && (
                <p className="text-xs text-amber-500">
                  Availability validation will be skipped. All equipment and operators are shown.
                </p>
              )}

              <DialogFooter>
                {/* Only show Remove button for non-docketed/invoiced jobs */}
                {getJobAllocation(selectedJobForAllocation.id) && 
                 selectedJobForAllocation.status !== 'docketed' && 
                 selectedJobForAllocation.status !== 'invoiced' && (
                  <Button type="button" variant="destructive" onClick={() => { handleDeleteAllocation(getJobAllocation(selectedJobForAllocation.id).id); setAllocationDialogOpen(false); }} onMouseDown={() => document.activeElement?.blur()}>
                    Remove
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => setAllocationDialogOpen(false)} onMouseDown={() => document.activeElement?.blur()}>
                  {selectedJobForAllocation.status === 'docketed' || selectedJobForAllocation.status === 'invoiced' ? 'Close' : 'Cancel'}
                </Button>
                {/* Only show Save button for non-docketed/invoiced jobs */}
                {selectedJobForAllocation.status !== 'docketed' && selectedJobForAllocation.status !== 'invoiced' && (
                  <Button 
                    type="button" 
                    className="btn-volt" 
                    disabled={
                      !allocationForm.operator_id || 
                      (allocationForm.is_combo_allocation 
                        ? allocationForm.combo_equipment_ids.some(c => !c.equipment_id)
                        : !allocationForm.equipment_id)
                    }
                    onMouseDown={(e) => {
                      // Blur any focused element to prevent date picker issues
                      if (document.activeElement) {
                        document.activeElement.blur();
                      }
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmitAllocation();
                    }}
                  >
                    Save Allocation
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QUICK ADD CUSTOMER DIALOG */}
      <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#ffd108]" />
              Quick Add Customer
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Company Name *</Label>
              <Input value={customerForm.company_name} onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })} placeholder="ABC Construction" required />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Contact Name *</Label>
              <Input value={customerForm.contact_name} onChange={(e) => setCustomerForm({ ...customerForm, contact_name: e.target.value })} placeholder="John Smith" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} placeholder="+1 234 567 890" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} placeholder="john@abc.com" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-volt"><Plus className="h-4 w-4 mr-2" />Add Customer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QUICK ADD CONTACT DIALOG */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5 text-[#ffd108]" />
              Add Contact
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateContact} className="space-y-4">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm text-muted-foreground">
                Adding contact for: <span className="font-medium text-foreground">{getCustomerInfo(jobForm.customer_id)?.company_name}</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Name *</Label>
              <Input
                data-testid="contact-name-input"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                placeholder="John Smith"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Position / Title</Label>
              <Input
                data-testid="contact-position-input"
                value={contactForm.position}
                onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                placeholder="Site Manager"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input
                  data-testid="contact-phone-input"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="0400 000 000"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="contact-submit-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QUICK ADD SITE DIALOG */}
      <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#ffd108]" />
              Quick Add Site
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSite} className="space-y-4">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm text-muted-foreground">
                Adding site for: <span className="font-medium text-foreground">{getCustomerInfo(jobForm.customer_id)?.company_name}</span>
              </p>
            </div>
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <div>
                <Label className="text-xs text-muted-foreground">Search Address</Label>
                <GooglePlacesAutocomplete
                  placeholder="Start typing an address..."
                  onPlaceSelect={(place) => {
                    setSiteForm(prev => ({
                      ...prev,
                      name: place.name || prev.name, // Populate site name if available
                      street_address: place.street_address || '',
                      suburb: place.suburb || '',
                      state: place.state || '',
                      postcode: place.postcode || ''
                    }));
                  }}
                  data-testid="site-address-autocomplete"
                />
                <p className="text-xs text-muted-foreground mt-1">Or enter manually below</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Site Name *</Label>
                <Input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="Site or business name" required />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Street Address *</Label>
                <Input
                  value={siteForm.street_address}
                  onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })}
                  placeholder="e.g., 123 Main Street"
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Suburb *</Label>
                  <Input
                    value={siteForm.suburb}
                    onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })}
                    placeholder="Suburb"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">State *</Label>
                  <Select 
                    value={siteForm.state} 
                    onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUSTRALIAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Post Code *</Label>
                  <Input
                    value={siteForm.postcode}
                    onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                    placeholder="0000"
                    maxLength={4}
                    required
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea value={siteForm.notes} onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })} placeholder="Access instructions..." rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-volt"><Plus className="h-4 w-4 mr-2" />Add Site</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CREATE DOCKET DIALOG - Time-based (for transcribing paper dockets) */}
      <Dialog open={docketDialogOpen} onOpenChange={setDocketDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-docket-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#ffd108]" />
              Create Docket
            </DialogTitle>
          </DialogHeader>
          
          {selectedJobForDocket && (
            <form onSubmit={handleSubmitDocket} className="space-y-6">
              {/* Job Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="label-overline text-xs">Date</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDateDisplay(selectedJobForDocket.start_date)}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Customer</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {getCustomerInfo(selectedJobForDocket.customer_id)?.company_name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Site</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {getSiteInfo(selectedJobForDocket.site_id)?.name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Asset</p>
                  <p className="font-medium flex items-center gap-1">
                    <Truck className="h-4 w-4" />
                    {selectedJobForDocket.equipment_type}
                  </p>
                </div>
              </div>

              {selectedJobForDocket.description && (
                <div className="p-3 border rounded-lg">
                  <p className="label-overline text-xs mb-1">Job Description</p>
                  <p className="text-sm">{selectedJobForDocket.description}</p>
                </div>
              )}

              {/* Docket Items - Time-based */}
              <div>
                <h3 className="font-heading font-semibold mb-3">Time & Hours</h3>
                <div className="space-y-4">
                  {docketItems.map((item) => (
                    <div key={item.id} className="p-4 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant={item.item_type === 'equipment' ? 'default' : 'secondary'}>
                          {item.item_type === 'equipment' ? 'Equipment' : 'Attachment'}
                        </Badge>
                        <span className="font-medium">{item.type_name}</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3">
                        {/* Start Time */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Start Time</Label>
                          <div className="flex gap-1">
                            <Select
                              value={item.start_time?.split(':')[0] || '08'}
                              onValueChange={(hour) => {
                                const mins = item.start_time?.split(':')[1] || '00';
                                handleDocketItemChange(item.id, 'start_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                                  <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="flex items-center text-muted-foreground">:</span>
                            <Select
                              value={item.start_time?.split(':')[1] || '00'}
                              onValueChange={(mins) => {
                                const hour = item.start_time?.split(':')[0] || '08';
                                handleDocketItemChange(item.id, 'start_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((mins) => (
                                  <SelectItem key={mins} value={mins}>{mins}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Finish Time */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Finish Time</Label>
                          <div className="flex gap-1">
                            <Select
                              value={item.finish_time?.split(':')[0] || '17'}
                              onValueChange={(hour) => {
                                const mins = item.finish_time?.split(':')[1] || '00';
                                handleDocketItemChange(item.id, 'finish_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                                  <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="flex items-center text-muted-foreground">:</span>
                            <Select
                              value={item.finish_time?.split(':')[1] || '00'}
                              onValueChange={(mins) => {
                                const hour = item.finish_time?.split(':')[0] || '17';
                                handleDocketItemChange(item.id, 'finish_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((mins) => (
                                  <SelectItem key={mins} value={mins}>{mins}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Empty space to align with Total Hours below */}
                        <div></div>
                      </div>
                      
                      {/* Second row: Lunch, Travel, Total Hours */}
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        {/* Lunch Break */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Lunch (mins)</Label>
                          <Select
                            value={String(item.lunch_minutes || 0)}
                            onValueChange={(v) => handleDocketItemChange(item.id, 'lunch_minutes', parseInt(v))}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 15, 30, 45, 60, 90, 120].map((mins) => (
                                <SelectItem key={mins} value={String(mins)}>{mins} min</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Travel Time */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Travel (mins)</Label>
                          <Select
                            value={String(item.travel_minutes || 0)}
                            onValueChange={(v) => handleDocketItemChange(item.id, 'travel_minutes', parseInt(v))}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 15, 30, 45, 60, 90, 120, 150, 180].map((mins) => (
                                <SelectItem key={mins} value={String(mins)}>{mins} min</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Total Hours */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Total Hours</Label>
                          <div className="h-9 px-3 flex items-center bg-muted rounded-md border font-mono font-medium text-[#ffd108]">
                            {(parseFloat(item.qty) || 0).toFixed(2)} hrs
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Formula: (Finish - Start) - Lunch + Travel
                </p>
              </div>

              {/* Material Loads Section */}
              <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="label-overline">Material Loads</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddMaterialLoad}
                      data-testid="add-material-load-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Load
                    </Button>
                  </div>
                  
                  {docketMaterialLoads.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                      No material loads added yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {docketMaterialLoads.map((load) => (
                        <div key={load.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant={load.load_type === 'pickup' ? 'default' : load.load_type === 'disposal' ? 'destructive' : 'secondary'}>
                                {load.load_type === 'pickup' ? 'Pickup' : load.load_type === 'disposal' ? 'Disposal' : 'Buy Back'}
                              </Badge>
                              <span className="font-medium">{load.material_type_name}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {load.quantity} {load.unit} from {load.yard_name}
                              {load.tip_docket_number && ` • Tip #${load.tip_docket_number}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMaterialLoad(load)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMaterialLoad(load.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              {/* Work Completed */}
              <div className="space-y-2">
                <Label className="label-overline">Work Completed</Label>
                <Textarea
                  value={docketWorkCompleted}
                  onChange={(e) => setDocketWorkCompleted(e.target.value)}
                  placeholder="Describe the work completed on this job..."
                  rows={3}
                  data-testid="docket-work-completed"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  value={docketNotes}
                  onChange={(e) => setDocketNotes(e.target.value)}
                  placeholder="Additional notes for this docket..."
                  rows={2}
                  data-testid="docket-notes"
                />
              </div>

              {/* Paper Docket Upload */}
              <div className="space-y-3">
                <Label className="label-overline">Paper Docket (Optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Upload a photo or scan of the original paper docket for your records.
                </p>
                
                {!docketPaperUrl ? (
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-[#ffd108]/50 transition-colors">
                    <input
                      type="file"
                      id="paper-docket-upload"
                      accept="image/*,.pdf"
                      onChange={handleDocketPaperUpload}
                      className="hidden"
                      disabled={docketUploading}
                    />
                    <label htmlFor="paper-docket-upload" className="cursor-pointer">
                      {docketUploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
                          <span className="text-sm text-muted-foreground">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="p-3 rounded-full bg-muted">
                            <FileText className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <span className="text-sm font-medium">Click to upload paper docket</span>
                          <span className="text-xs text-muted-foreground">PNG, JPG, PDF up to 10MB</span>
                        </div>
                      )}
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-green-500/20">
                        <Check className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{docketPaperName}</p>
                        <a 
                          href={docketPaperUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-[#ffd108] hover:underline flex items-center gap-1"
                        >
                          View document <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteDocketPaper}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDocketDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-volt" data-testid="save-docket-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  Save Docket
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* MATERIAL LOAD DIALOG */}
      <Dialog open={showMaterialLoadDialog} onOpenChange={(open) => {
        if (!open) {
          setShowMaterialLoadDialog(false);
          setEditingMaterialLoad(null);
        }
      }}>
        <DialogContent className="max-w-md" data-testid="material-load-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-[#ffd108]" />
              {editingMaterialLoad?.id.startsWith('load-') ? 'Add Material Load' : 'Edit Material Load'}
            </DialogTitle>
          </DialogHeader>
          
          {editingMaterialLoad && (
            <div className="space-y-4 py-2">
              {/* Load Type */}
              <div className="space-y-2">
                <Label className="label-overline">Load Type *</Label>
                <Select
                  value={editingMaterialLoad.load_type}
                  onValueChange={(v) => setEditingMaterialLoad(prev => ({ ...prev, load_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickup">Pickup (load onto truck)</SelectItem>
                    <SelectItem value="disposal">Disposal (tip off)</SelectItem>
                    <SelectItem value="buy_back">Buy Back</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Yard */}
              <div className="space-y-2">
                <Label className="label-overline">Yard *</Label>
                <Select
                  value={editingMaterialLoad.yard_id}
                  onValueChange={handleMaterialLoadYardChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select yard" />
                  </SelectTrigger>
                  <SelectContent>
                    {yards.map((yard) => (
                      <SelectItem key={yard.id} value={yard.id}>{yard.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Material Type */}
              <div className="space-y-2">
                <Label className="label-overline">Material Type *</Label>
                <Select
                  value={editingMaterialLoad.material_type_id}
                  onValueChange={handleMaterialLoadTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialTypes.filter(t => t.is_active !== false).map((matType) => (
                      <SelectItem key={matType.id} value={matType.id}>{matType.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Quantity & Unit */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">Quantity *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editingMaterialLoad.quantity}
                    onChange={(e) => setEditingMaterialLoad(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                    placeholder="0"
                    data-testid="material-load-qty"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Unit *</Label>
                  <Select
                    value={editingMaterialLoad.unit}
                    onValueChange={(v) => setEditingMaterialLoad(prev => ({ ...prev, unit: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tonne">Tonne</SelectItem>
                      <SelectItem value="cubic_metre">Cubic Metre (m³)</SelectItem>
                      <SelectItem value="load">Load</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Tip Docket Number (for disposal) */}
              {editingMaterialLoad.load_type === 'disposal' && (
                <div className="space-y-2">
                  <Label className="label-overline">Tip Docket Number</Label>
                  <Input
                    value={editingMaterialLoad.tip_docket_number || ''}
                    onChange={(e) => setEditingMaterialLoad(prev => ({ ...prev, tip_docket_number: e.target.value }))}
                    placeholder="Enter tip docket number"
                    data-testid="tip-docket-number"
                  />
                </div>
              )}
              
              {/* Tip Docket Photo (for disposal) */}
              {editingMaterialLoad.load_type === 'disposal' && (
                <div className="space-y-2">
                  <Label className="label-overline">Tip Docket Photo</Label>
                  {!editingMaterialLoad.tip_docket_photo_url ? (
                    <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-[#ffd108]/50 transition-colors">
                      <input
                        type="file"
                        id="tip-docket-photo"
                        accept="image/*"
                        onChange={handleTipDocketPhotoUpload}
                        className="hidden"
                      />
                      <label htmlFor="tip-docket-photo" className="cursor-pointer flex flex-col items-center gap-1">
                        <Camera className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Upload tip docket photo</span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <a 
                          href={editingMaterialLoad.tip_docket_photo_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-[#ffd108] hover:underline"
                        >
                          View photo
                        </a>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingMaterialLoad(prev => ({ ...prev, tip_docket_photo_url: '' }))}
                        className="text-destructive hover:text-destructive h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Input
                  value={editingMaterialLoad.notes || ''}
                  onChange={(e) => setEditingMaterialLoad(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setShowMaterialLoadDialog(false);
              setEditingMaterialLoad(null);
            }}>
              Cancel
            </Button>
            <Button type="button" className="btn-volt" onClick={handleSaveMaterialLoad} data-testid="save-material-load-btn">
              Save Load
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXTEND ALLOCATIONS MODAL */}
      <Dialog open={extendAllocationsModal.open} onOpenChange={(open) => !open && handleExtendAllocationsCancel()}>
        <DialogContent className="sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-volt" />
              Extend Allocations?
            </DialogTitle>
            <DialogDescription>
              You are extending the job end date. Would you like to extend existing allocations to match?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Job Info */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium text-sm">{extendAllocationsModal.jobDescription}</p>
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>New end date: <strong className="text-foreground">{formatDateDisplay(extendAllocationsModal.newEndDate)}</strong></span>
              </div>
            </div>
            
            {/* Allocations to extend */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Allocations that can be extended:</Label>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {extendAllocationsModal.allocations.map((alloc) => (
                  <div 
                    key={alloc.id} 
                    className="flex items-center justify-between p-2 bg-muted/50 rounded-md border text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{alloc.operator_name}</span>
                      <span className="text-muted-foreground">({alloc.operator_code})</span>
                    </div>
                    <div className="text-muted-foreground">
                      {formatDateDisplay(alloc.start_date)} - {formatDateDisplay(alloc.end_date || alloc.start_date)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Extension preview */}
            <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <ArrowRight className="h-4 w-4" />
                <span className="text-sm">
                  {extendAllocationsModal.allocations.length} allocation{extendAllocationsModal.allocations.length !== 1 ? 's' : ''} will be extended to <strong>{formatDateDisplay(extendAllocationsModal.newEndDate)}</strong>
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => handleExtendAllocationsConfirm(false)}
              className="w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              Update Job Only
            </Button>
            <Button
              onClick={() => handleExtendAllocationsConfirm(true)}
              className="btn-volt w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              <CalendarDays className="h-4 w-4 mr-2" />
              Extend Allocations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXTEND JOB MODAL */}
      <Dialog open={extendJobModal.open} onOpenChange={(open) => !open && setExtendJobModal(prev => ({ ...prev, open: false }))}>
        <DialogContent className="sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-volt" />
              Extend Job
            </DialogTitle>
            <DialogDescription>
              Extend the job duration by adding more days
            </DialogDescription>
          </DialogHeader>
          
          {extendJobModal.job && (
            <div className="space-y-4 py-4">
              {/* Job Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{extendJobModal.job.description || 'No description'}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Current dates: {formatDateDisplay(extendJobModal.job.start_date)}
                  {extendJobModal.job.end_date && extendJobModal.job.end_date !== extendJobModal.job.start_date && (
                    <> - {formatDateDisplay(extendJobModal.job.end_date)}</>
                  )}
                </p>
              </div>
              
              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Extension Starts</Label>
                  <div className="flex h-9 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm font-mono items-center">
                    {extendJobModal.dateFrom ? formatDateDisplay(extendJobModal.dateFrom) : '-'}
                  </div>
                  <p className="text-xs text-muted-foreground">Day after current end date</p>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">New End Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-mono h-9 ${!extendJobModal.dateTo && 'text-muted-foreground'}`}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {extendJobModal.dateTo ? formatDateDisplay(extendJobModal.dateTo) : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={extendJobModal.dateTo ? parse(extendJobModal.dateTo, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setExtendJobModal(prev => ({ ...prev, dateTo: format(date, 'yyyy-MM-dd') }));
                          }
                        }}
                        disabled={(date) => {
                          const minDate = extendJobModal.dateFrom ? parse(extendJobModal.dateFrom, 'yyyy-MM-dd', new Date()) : new Date();
                          return date < minDate;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              {/* Extend Allocations Option */}
              {extendJobModal.allocations.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="extend-allocations-checkbox"
                      checked={extendJobModal.extendAllocations}
                      onChange={(e) => setExtendJobModal(prev => ({ ...prev, extendAllocations: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="extend-allocations-checkbox" className="text-sm font-medium cursor-pointer">
                      Extend existing allocations to new end date
                    </Label>
                  </div>
                  
                  {extendJobModal.extendAllocations && (
                    <div className="ml-6 space-y-2">
                      <p className="text-xs text-muted-foreground">The following allocations will be extended:</p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {extendJobModal.allocations.map((alloc) => (
                          <div 
                            key={alloc.id}
                            className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <User className="h-3 w-3 text-muted-foreground" />
                              <span>{alloc.operator_name}</span>
                            </div>
                            <div className="text-muted-foreground text-xs">
                              {formatDateDisplay(alloc.end_date || alloc.start_date)} → {extendJobModal.dateTo ? formatDateDisplay(extendJobModal.dateTo) : '...'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {extendJobModal.allocations.length === 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    <span>This job has no allocations. Only the job dates will be extended.</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setExtendJobModal(prev => ({ ...prev, open: false }))}
              disabled={extendJobModal.loading}
              className="w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExtendJobSubmit}
              disabled={!extendJobModal.dateTo || extendJobModal.loading}
              className="btn-volt w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              {extendJobModal.loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CalendarPlus className="h-4 w-4 mr-2" />
              )}
              Extend Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Single Docket Drawer - Full featured with Tabs like Dockets page */}
      <Sheet open={viewDocketDrawerOpen} onOpenChange={setViewDocketDrawerOpen}>
        <SheetContent side="right" className="w-full max-w-[1200px] sm:w-[1200px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#ffd108]" />
              Docket #{viewingDocket?.docket_number}
            </SheetTitle>
          </SheetHeader>
          
          {viewingDocket && (
            <Tabs defaultValue="docket" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="docket" className="flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" />
                  Docket
                </TabsTrigger>
                <TabsTrigger value="invoice" className="flex items-center gap-2">
                  <FileDown className="h-4 w-4" />
                  Invoice
                </TabsTrigger>
              </TabsList>
              
              {/* Docket Tab - Submitted Docket Data */}
              <TabsContent value="docket" className="space-y-6 pt-4">
                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="label-overline text-xs">Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDateDisplay(viewingDocket.job_date)}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Customer</p>
                    <p className="font-medium flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {viewingDocket.customer_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Site</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {viewingDocket.site_name || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Status</p>
                    <Badge className={
                      viewingDocket.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                      viewingDocket.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-600' :
                      viewingDocket.status === 'exported' ? 'bg-blue-500/20 text-blue-600' :
                      'bg-gray-500/20 text-gray-600'
                    }>
                      {viewingDocket.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                    </Badge>
                  </div>
                </div>

                {/* Submitted Hours */}
                <div>
                  <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ffd108]" />
                    Hours Recorded
                  </h3>
                  <div className="space-y-2">
                    {viewingDocket.items?.map((item, idx) => (
                      <div key={item.id || idx} className="bg-muted/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {item.item_type === 'equipment' ? (
                              <div className="w-8 h-8 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                                <Truck className="h-4 w-4 text-[#ffd108]" />
                              </div>
                            ) : item.item_type === 'worker' ? (
                              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-500" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-500/20 flex items-center justify-center">
                                <Wrench className="h-4 w-4 text-gray-500" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{item.display_name || item.type_name}</p>
                              {item.description && (
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                              )}
                              {/* Start Time and Finish Time with labels */}
                              {(item.start_time || item.finish_time) && (
                                <div className="flex items-center gap-4 mt-1 text-xs">
                                  {item.start_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">Start Time:</span>
                                      <span className="font-mono text-foreground">{item.start_time}</span>
                                    </span>
                                  )}
                                  {item.finish_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">Finish Time:</span>
                                      <span className="font-mono text-foreground">{item.finish_time}</span>
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold text-[#ffd108]">{item.qty || item.quantity}h</p>
                          </div>
                        </div>
                        {/* Lunch & Travel breakdown */}
                        {(item.lunch_minutes > 0 || item.travel_minutes > 0) && (
                          <div className="flex items-center gap-4 mt-2 ml-11 text-xs text-muted-foreground">
                            {item.lunch_minutes > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="text-amber-500">Lunch:</span> -{item.lunch_minutes}min
                              </span>
                            )}
                            {item.travel_minutes > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="text-blue-500">Travel:</span> +{item.travel_minutes}min
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Work Completed */}
                {viewingDocket.work_completed && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Work Completed</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">{viewingDocket.work_completed}</p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {viewingDocket.notes && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Docket Notes</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">{viewingDocket.notes}</p>
                    </div>
                  </div>
                )}

                {/* Customer Sign-off */}
                {(viewingDocket.customer_signoff_confirmed || viewingDocket.signature_data) && (
                  <div className="border-t pt-4">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Customer Sign-off
                    </h3>
                    <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div>
                        <p className="font-medium">{viewingDocket.customer_signoff_name || viewingDocket.signed_by || 'Customer'}</p>
                        <p className="text-sm text-muted-foreground">Confirmed & Signed</p>
                      </div>
                      {(viewingDocket.customer_signoff_signature || viewingDocket.signature_data) && (
                        <div className="ml-auto bg-white rounded p-2">
                          <img 
                            src={viewingDocket.customer_signoff_signature || viewingDocket.signature_data} 
                            alt="Customer Signature" 
                            className="h-12 max-w-[150px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submitted Timestamp */}
                {viewingDocket.created_at && (
                  <div className="text-sm text-muted-foreground text-right pt-2 border-t">
                    Submitted: {new Date(viewingDocket.created_at).toLocaleString()}
                  </div>
                )}
              </TabsContent>
              
              {/* Invoice Tab - Editable Line Items */}
              <TabsContent value="invoice" className="space-y-6 pt-4">
                {viewingDocket?.status === 'draft' ? (
                  <p className="text-sm text-muted-foreground">
                    Edit line items and pricing for invoicing. Changes cannot be made after approval.
                  </p>
                ) : (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      This docket is <strong>{viewingDocket?.status?.replace('_', ' ')}</strong>. Invoice details are read-only.
                    </p>
                  </div>
                )}
                
                {/* Header Info - Read Only */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="label-overline text-xs">Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDateDisplay(viewingDocket.job_date)}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Customer</p>
                    <p className="font-medium flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {viewingDocket.customer_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Site</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {viewingDocket.site_name || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Status</p>
                    <Badge className={
                      viewingDocket.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                      viewingDocket.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-600' :
                      viewingDocket.status === 'exported' ? 'bg-blue-500/20 text-blue-600' :
                      'bg-gray-500/20 text-gray-600'
                    }>
                      {viewingDocket.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                    </Badge>
                  </div>
                </div>

                {/* Price List Indicator */}
                {docketUsedPriceList && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm">
                      Prices calculated using: <span className="font-semibold text-green-700 dark:text-green-300">{docketUsedPriceList}</span>
                    </span>
                  </div>
                )}

                {/* Editable Items Table */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold">Docket Items</h3>
                    <div className="flex items-center gap-2">
                      {viewingDocket?.status === 'draft' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDocketRecalculatePrices}
                            disabled={docketRecalculating}
                            className="flex items-center gap-2"
                          >
                            {docketRecalculating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                            Recalculate
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDocketAddLineItem}
                            className="flex items-center gap-2"
                            data-testid="add-line-item-btn"
                          >
                            <Plus className="h-4 w-4" />
                            Add Line
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table className="text-sm">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="label-overline w-[100px] text-xs px-2">Account ID</TableHead>
                          <TableHead className="label-overline text-xs px-2">Description</TableHead>
                          <TableHead className="label-overline w-[80px] text-xs px-2">Qty</TableHead>
                          <TableHead className="label-overline w-[80px] text-xs px-2">UOM</TableHead>
                          <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Price</TableHead>
                          <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Total</TableHead>
                          {viewingDocket?.status === 'draft' && (
                            <TableHead className="label-overline w-[40px] text-xs px-1"></TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {docketEditItems.map((item) => (
                          <TableRow key={item.id} className="h-10">
                            <TableCell className="px-2 py-1">
                              <Input
                                value={item.account_item_id || ''}
                                onChange={(e) => handleDocketEditItemChange(item.id, 'account_item_id', e.target.value)}
                                placeholder="ID"
                                className="font-mono text-xs h-7 px-2"
                                disabled={viewingDocket?.status !== 'draft'}
                              />
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <div className="flex items-center gap-2">
                                {item.item_type && (
                                  <Badge 
                                    variant={item.item_type === 'equipment' ? 'default' : 'secondary'} 
                                    className={`text-[10px] px-1.5 py-0 shrink-0 ${item.item_type === 'worker' ? 'bg-blue-600 text-white' : ''}`}
                                  >
                                    {item.item_type === 'equipment' ? 'E' : item.item_type === 'worker' ? 'L' : 'A'}
                                  </Badge>
                                )}
                                <Input
                                  value={item.description || item.display_name || item.type_name || ''}
                                  onChange={(e) => handleDocketEditItemChange(item.id, 'description', e.target.value)}
                                  placeholder="Line item description..."
                                  className="text-xs h-7 px-2 flex-1"
                                  disabled={viewingDocket?.status !== 'draft'}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <Input
                                type="number"
                                step="0.5"
                                min="0"
                                value={item.qty || item.quantity || 0}
                                onChange={(e) => handleDocketEditItemChange(item.id, 'qty', parseFloat(e.target.value) || 0)}
                                className="font-mono text-xs h-7 px-2 w-full text-center"
                                disabled={viewingDocket?.status !== 'draft'}
                              />
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <Select
                                value={item.uom || 'HOUR'}
                                onValueChange={(v) => handleDocketEditItemChange(item.id, 'uom', v)}
                                disabled={viewingDocket?.status !== 'draft'}
                              >
                                <SelectTrigger className="h-7 text-xs px-2">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="HOUR">HOUR</SelectItem>
                                  <SelectItem value="DAY">DAY</SelectItem>
                                  <SelectItem value="WEEK">WEEK</SelectItem>
                                  <SelectItem value="EACH">EACH</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="px-2 py-1 text-right">
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                value={item.unit_price || item.rate || 0}
                                onChange={(e) => handleDocketEditItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="font-mono text-xs h-7 px-2 text-right w-full"
                                disabled={viewingDocket?.status !== 'draft'}
                              />
                            </TableCell>
                            <TableCell className="px-2 py-1 text-right font-mono text-xs font-medium">
                              {tenantSettings.currency_symbol}{((parseFloat(item.qty || item.quantity || 0)) * (parseFloat(item.unit_price || item.rate || 0))).toFixed(2)}
                            </TableCell>
                            {viewingDocket?.status === 'draft' && (
                              <TableCell className="px-1 py-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDocketRemoveLineItem(item.id)}
                                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                  data-testid={`remove-line-${item.id}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Totals Section */}
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <div className="w-64 space-y-2">
                      {(() => {
                        const subtotal = docketEditItems.reduce((sum, item) => sum + ((parseFloat(item.qty || item.quantity || 0)) * (parseFloat(item.unit_price || item.rate || 0))), 0);
                        const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                        const taxAmount = subtotal * (taxRate / 100);
                        const total = subtotal + taxAmount;
                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Subtotal</span>
                              <span className="font-mono">{tenantSettings.currency_symbol}{subtotal.toFixed(2)}</span>
                            </div>
                            {taxRate > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{tenantSettings.tax_name} ({taxRate}%)</span>
                                <span className="font-mono">{tenantSettings.currency_symbol}{taxAmount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between pt-2 border-t">
                              <span className="font-semibold">Total</span>
                              <span className="text-xl font-bold font-mono text-[#ffd108]">
                                {tenantSettings.currency_symbol}{total.toFixed(2)}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="label-overline">Notes</Label>
                  <Textarea
                    value={docketEditNotes}
                    onChange={(e) => setDocketEditNotes(e.target.value)}
                    placeholder="Additional notes for this docket..."
                    rows={2}
                    disabled={viewingDocket?.status !== 'draft'}
                  />
                </div>

                {/* Save button for draft dockets only - shown inline in Invoice tab */}
                {viewingDocket?.status === 'draft' && (
                  <div className="flex justify-end pt-4 border-t mt-6">
                    <Button onClick={handleDocketSaveEdit} className="btn-volt" disabled={docketSaving}>
                      {docketSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          {/* Footer for Docket Tab - Always visible */}
          <SheetFooter className="pt-4 border-t mt-6 flex-wrap gap-2">
            <div className="flex gap-2 flex-1 flex-wrap">
              {viewingDocket?.status === 'approved' && (
                <>
                  <Button variant="outline" onClick={() => handleGenerateDocketPDF(viewingDocket)}>
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF Invoice
                  </Button>
                  {tenantSettings.xero_connected && !viewingDocket.xero_invoice_id && (
                    <Button variant="outline" onClick={() => handleExportDocketToXero(viewingDocket)}>
                      <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                      Export to Xero
                    </Button>
                  )}
                  {tenantSettings.myob_connected && !viewingDocket.myob_invoice_id && (
                    <Button variant="outline" onClick={() => handleExportDocketToMyob(viewingDocket)}>
                      <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
                      Export to MYOB
                    </Button>
                  )}
                </>
              )}
              {viewingDocket?.status === 'draft' && (
                <Button variant="outline" onClick={() => handleSubmitDocketForApproval(viewingDocket.id)}>
                  <Send className="h-4 w-4 mr-2" />
                  Submit for Approval
                </Button>
              )}
              {viewingDocket?.status === 'pending_approval' && (
                <>
                  <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleApproveDocketFromDrawer(viewingDocket.id)}>
                    <Check className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => handleRejectDocketFromDrawer(viewingDocket.id)}>
                    <X className="h-4 w-4 mr-2" />
                    Return to Draft
                  </Button>
                </>
              )}
            </div>
            <Button variant="outline" onClick={() => setViewDocketDrawerOpen(false)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Dockets List Drawer (for multi-day jobs) */}
      <Sheet open={docketsListDrawerOpen} onOpenChange={setDocketsListDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-500" />
              Job Dockets
              {selectedJobForDocketsList && (
                <Badge variant="secondary">
                  Job #{selectedJobForDocketsList.job_number}
                </Badge>
              )}
            </SheetTitle>
          </SheetHeader>
          
          <div className="py-4">
            {selectedJobForDocketsList && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{customers.find(c => c.id === selectedJobForDocketsList.customer_id)?.company_name || 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDateDisplay(selectedJobForDocketsList.start_date)} - {formatDateDisplay(selectedJobForDocketsList.end_date)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{jobDocketsList.length} docket(s)</p>
              </div>
            )}
            
            <div className="space-y-2">
              {jobDocketsList.map((docket) => (
                <div 
                  key={docket.id}
                  className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleOpenDocketFromList(docket)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{formatDateDisplay(docket.job_date)}</p>
                      <p className="text-sm text-muted-foreground">
                        {docket.start_time || '-'} - {docket.finish_time || '-'}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        docket.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                        docket.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-600' :
                        docket.status === 'exported' ? 'bg-blue-500/20 text-blue-600' :
                        'bg-gray-500/20 text-gray-600'
                      }>
                        {docket.status?.replace('_', ' ')}
                      </Badge>
                      <p className="text-sm font-medium mt-1">
                        {(() => {
                          const subtotal = docket.items?.reduce((sum, item) => sum + (parseFloat(item.qty || item.quantity || 0)) * (parseFloat(item.unit_price || item.rate || 0)), 0) || 0;
                          const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                          const total = subtotal * (1 + taxRate / 100);
                          return `${tenantSettings.currency_symbol}${total.toFixed(2)}`;
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {jobDocketsList.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No dockets found for this job</p>
              )}
            </div>
          </div>
          
          <SheetFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDocketsListDrawerOpen(false)}>Close</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Allocation Conflict Warning Dialog */}
      <AlertDialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Allocation Conflict Detected
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  The following conflicts were found. Do you want to proceed anyway?
                </p>
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {conflictWarnings.map((warning, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 rounded-lg"
                    >
                      <div className="flex items-start gap-2">
                        {warning.type === 'operator_conflict' && (
                          <User className="h-4 w-4 text-amber-600 mt-0.5" />
                        )}
                        {warning.type === 'equipment_conflict' && (
                          <Truck className="h-4 w-4 text-amber-600 mt-0.5" />
                        )}
                        {warning.type === 'worker_conflict' && (
                          <Users className="h-4 w-4 text-amber-600 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                            {warning.type === 'operator_conflict' && 'Operator Conflict'}
                            {warning.type === 'equipment_conflict' && 'Equipment Conflict'}
                            {warning.type === 'worker_conflict' && 'Worker Conflict'}
                          </p>
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            {warning.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setConflictWarnings([]);
              setPendingAllocationData(null);
              setConflictContext(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={proceedWithAllocation}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Proceed Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Job Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteJobDialogOpen}
        onOpenChange={setDeleteJobDialogOpen}
        title="Delete Job"
        description={`Delete job "${jobToDelete?.job_number || ''}"? This will also delete all associated allocations. This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDeleteJob}
        isLoading={isDeletingJob}
        testId="delete-job-dialog"
      />
    </div>
  );
};

export default Schedule;
